/**
 * Minimal API client. All requests are same-origin in production (the
 * operator app at app.satatam.co and the backend
 * routes are mounted on the same domain via a shared Cloudflare Worker
 * route binding), and proxied via Vite in dev.
 *
 * The cspro_session cookie is the only auth credential. It's HttpOnly so
 * the JS layer never sees the token directly; we set credentials: 'include'
 * on every request so the browser attaches the cookie automatically.
 */

const BASE = (import.meta.env.VITE_API_BASE_URL || "").replace(/\/+$/, "");

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

async function request<T>(
  method: Method,
  path: string,
  body?: unknown,
  signal?: AbortSignal,
  extraHeaders?: Record<string, string>,
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  let payload: BodyInit | undefined;
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }
  // The caller's headers merge LAST. The only one that exists today is
  // `Idempotency-Key`, which has to survive to the server or a retried write
  // becomes a second write; and a caller that deliberately restates
  // Content-Type is saying so on purpose.
  Object.assign(headers, extraHeaders);
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: payload,
    credentials: "include",
    signal,
  });
  const text = await res.text();
  let parsed: unknown = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    // Non-JSON response (e.g., HTML error page). Keep parsed as null.
  }
  if (!res.ok) {
    // A route that sends a human sentence has already said the thing better
    // than any code lookup here could. `error` stays the fallback, and
    // `ApiError.body` keeps the machine code for anything that switches on it.
    const record = parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : null;
    const message = record && typeof record.message === "string" && record.message.trim() ? record.message : null;
    const code = record && "error" in record ? String(record.error) : null;
    throw new ApiError(res.status, message ?? code ?? `${res.status} ${res.statusText}`, parsed);
  }
  return parsed as T;
}

/**
 * The absolute URL of an API path.
 *
 * For the handful of flows the browser must NAVIGATE to rather than fetch —
 * single sign-on hands the visitor to their identity provider and gets them
 * back on a redirect, which no XHR can follow.
 */
export function apiUrl(path: string): string {
  return `${BASE}${path}`;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>("GET", path, undefined, signal),
  post: <T>(path: string, body?: unknown, signal?: AbortSignal, headers?: Record<string, string>) =>
    request<T>("POST", path, body, signal, headers),
  put: <T>(path: string, body?: unknown, signal?: AbortSignal) => request<T>("PUT", path, body, signal),
  patch: <T>(path: string, body?: unknown, signal?: AbortSignal) => request<T>("PATCH", path, body, signal),
  del: <T>(path: string, signal?: AbortSignal) => request<T>("DELETE", path, undefined, signal),
};

// ---------------------------------------------------------------------------
// Receptionist builder — tenant identity and origin authority stay server-side.
// These payloads intentionally contain only operator-entered facts and the
// server-issued revision fence.
// ---------------------------------------------------------------------------

export interface ReceptionistFact {
  fact_id: string;
  field: string;
  value: string;
  approved: boolean;
  conflict: boolean;
  provenance?: {
    source_type?: string;
    source_ref?: string | null;
    snippet?: string;
    confidence?: number;
    extracted_at?: string;
  };
}

export interface ReceptionistDraft {
  revision: number;
  facts: ReceptionistFact[];
  conflicts: string[];
  critical_gaps: string[];
  kb_revision?: string | null;
  knowledge_stale?: boolean;
  provider_sync_state?: string;
  configuration?: Record<string, unknown> | null;
  activation?: { eligible?: boolean; reason?: string };
  source_fingerprint?: string;
  rollback_of_version?: number;
  [key: string]: unknown;
}

export interface ReceptionistResponse<T> {
  ok: boolean;
  result: T | null;
  error: string | null;
}

export interface ReceptionistVersion {
  version: number;
  sync_state?: string;
  provider_agent_id?: string | null;
  error?: string | null;
  created_at?: string;
  [key: string]: unknown;
}

/** Allowlisted, PII-free metadata for one completed receptionist call. */
export interface ReceptionistCall {
  /** Opaque persistence key; the operator UI must not display it. */
  call_id: string;
  status: "completed";
  started_at: string | null;
  answered_at: string | null;
  ended_at: string | null;
  /** Provider-reported usage, independent of Twilio's billable duration. */
  provider_usage_seconds: number | null;
  twilio_billable_seconds: number | null;
}

export const receptionistApi = {
  status: (signal?: AbortSignal) =>
    api.get<ReceptionistResponse<ReceptionistDraft>>("/api/receptionist/status", signal),
  callHistory: (signal?: AbortSignal) =>
    api.get<ReceptionistResponse<ReceptionistCall[]>>("/api/receptionist/calls?limit=20", signal),
  versions: (signal?: AbortSignal) =>
    api.get<ReceptionistResponse<ReceptionistVersion[]>>("/api/receptionist/versions", signal),
  createDraft: (intake: Record<string, string>, expectedRevision?: number) =>
    api.post<ReceptionistResponse<ReceptionistDraft>>("/api/receptionist/draft", { intake, ...(expectedRevision === undefined ? {} : { expected_revision: expectedRevision }) }),
  importProfile: (expectedRevision?: number) =>
    api.post<ReceptionistResponse<ReceptionistDraft>>("/api/receptionist/draft/import-profile", expectedRevision === undefined ? {} : { expected_revision: expectedRevision }),
  importCanonical: (revision: number) =>
    api.post<ReceptionistResponse<ReceptionistDraft>>("/api/receptionist/draft/import-canonical", { revision }),
  addFaq: (revision: number, question: string, answer: string) =>
    api.post<ReceptionistResponse<ReceptionistDraft>>("/api/receptionist/faq", { revision, question, answer }),
  reviewFacts: (revision: number, facts: ReceptionistFact[]) =>
    api.put<ReceptionistResponse<ReceptionistDraft>>("/api/receptionist/facts", { revision, facts }),
  generate: (revision: number) =>
    api.post<ReceptionistResponse<ReceptionistDraft>>("/api/receptionist/generate", { revision }),
  sync: (revision: number) =>
    api.post<ReceptionistResponse<ReceptionistVersion>>("/api/receptionist/sync", { revision }),
  activate: (revision: number) =>
    api.post<ReceptionistResponse<Record<string, never>>>("/api/receptionist/activate", { revision }),
  rollback: (version: number, revision: number) =>
    api.post<ReceptionistResponse<ReceptionistDraft>>(
      `/api/receptionist/versions/${encodeURIComponent(String(version))}/rollback`,
      { revision },
    ),
};

// ---------------------------------------------------------------------------
// Business profile -- the shared, tenant-scoped business facts
// (src/business_profile/routes.py). `get` is the published, approved-only
// projection (D4). The W3 write contract: `getDraft` returns every field's
// stored draft with provenance and its `revision`; `saveDraft` (any member)
// and `approve` (owner only) send revisions back so a concurrent change is a
// 409 `business_profile_changed`, never a silent overwrite or an approval of
// text nobody saw. Settings' Business profile card is the one caller.
// ---------------------------------------------------------------------------
export interface BusinessProfileFact {
  value: unknown;
  source: string | null;
  approved: boolean;
}

export interface BusinessProfile {
  name: BusinessProfileFact;
  hours: BusinessProfileFact;
  services: BusinessProfileFact;
  faqs: BusinessProfileFact;
  escalation: BusinessProfileFact;
  location: BusinessProfileFact;
  phone: BusinessProfileFact;
  escalation_contacts: BusinessProfileFact;
}

export interface BusinessProfileDraftFact {
  value: unknown;
  status: "draft" | "approved" | null;
  source: string | null;
  updated_by: string | null;
  updated_at: string | null;
  approved_by: string | null;
  approved_at: string | null;
  revision: string | null;
}

export interface BusinessProfileDraftResponse {
  ok: boolean;
  error: string | null;
  result: { draft: Record<string, BusinessProfileDraftFact> } | null;
}

export interface BusinessProfileApproveResponse {
  ok: boolean;
  error: string | null;
  result: {
    approved: string[];
    skipped: string[];
    already_approved: string[];
    draft: Record<string, BusinessProfileDraftFact>;
  } | null;
}

export interface BusinessProfileResponse {
  ok: boolean;
  error: string | null;
  result: { profile: BusinessProfile } | null;
}

export const businessProfileApi = {
  get: (signal?: AbortSignal) =>
    api.get<BusinessProfileResponse>("/api/business-profile", signal),
  getDraft: (signal?: AbortSignal) =>
    api.get<BusinessProfileDraftResponse>("/api/business-profile/draft", signal),
  saveDraft: (body: { fields: Record<string, string>; expected: Record<string, string | null> }) =>
    api.put<BusinessProfileDraftResponse>("/api/business-profile/draft", body),
  approve: (body: { fields: { name: string; revision: string | null }[] }) =>
    api.post<BusinessProfileApproveResponse>("/api/business-profile/approve", body),
};

// ---------------------------------------------------------------------------
// Typed endpoints that mirror the backend routes shipped 2026-05-26.
// ---------------------------------------------------------------------------

export interface AuthRequestLinkResponse {
  ok: boolean;
  message?: string;
  error?: string;
}

export interface AuthVerifyResponse {
  ok: boolean;
  tenant_id?: string;
  email?: string;
  error?: string;
}

export interface AuthLoginResponse {
  ok: boolean;
  tenant_id?: string;
  email?: string;
  error?: string;
  needs_verification?: boolean;
}

export interface AuthSignupResponse {
  ok: boolean;
  message?: string;
  error?: string;
  status?: number; // HTTP status carried through on error (e.g. 409 = already exists)
}

export interface AuthResendVerificationResponse {
  ok: boolean;
  error?: string;
}

export interface AuthSetPasswordResponse {
  ok: boolean;
  message?: string;
  error?: string;
  status?: number; // HTTP status carried through on error (401 = session expired, 400 = weak, 429 = throttled)
  code?: string; // e.g. "current_password_required"
}

export interface PlaygroundScenariosResponse {
  ok: boolean;
  scenarios: string[];
}

export interface PlaygroundUsageResponse {
  ok: boolean;
  turns_used: number;
  turn_cap: number | null;
  window_days: number;
}

export interface PlaygroundCall {
  tool: string;
  kwargs: Record<string, unknown>;
}

export interface PlaygroundRunResponse {
  ok: boolean;
  scenario_id: string;
  reply_text: string | null;
  signals: { intent?: string; sentiment?: string } | null;
  calls: PlaygroundCall[];
  turns_used: number;
  /** The trial ceiling, or null when this tenant's playground is uncapped —
   *  every paid plan. A number here is a promise the server will enforce it. */
  turn_cap: number | null;
  tone?: string;
  response_language?: string | null;
  error?: string;
  next_step?: "upgrade" | string;
}

export interface PlaygroundLanguage {
  code: string;
  endonym: string;
  english: string;
}

export interface PlaygroundOptionsResponse {
  ok: boolean;
  tones: string[];
  languages: PlaygroundLanguage[];
}

export interface PlaygroundRunOpts {
  tone?: string;
  language?: string;
}

export interface AuthMeResponse {
  ok: boolean;
  result?: {
    email: string;
    tenant_id: string;
    has_password: boolean;
    /** I-4: a fresh magic-link session may set a new password without the current one. */
    password_reset_without_current?: boolean;
  };
  error?: string;
}

export interface AuthAcceptInviteResponse {
  ok: boolean;
  result?: { tenant_id: string; email: string };
  error?: string;
}

export const authApi = {
  requestLink: (email: string) =>
    api.post<AuthRequestLinkResponse>("/api/auth/request-link", { email }),
  verify: (token: string) =>
    api.post<AuthVerifyResponse>("/api/auth/verify", { token }),

  /**
   * Team-invite acceptance — single-use token plus the password the invitee
   * chose. The server checks the password BEFORE redeeming the token, so a
   * 400 here leaves the invite usable; the page keeps the form up.
   */
  acceptInvite: (token: string, password: string) =>
    api.post<AuthAcceptInviteResponse>("/api/auth/accept-invite", { token, password }),

  /** Session identity — email + whether a password is already set. */
  me: (signal?: AbortSignal) => api.get<AuthMeResponse>("/api/auth/me", signal),

  /**
   * Email + password login. The shared `request()` helper THROWS an ApiError
   * on non-2xx, so we catch it here and normalize back into the
   * AuthLoginResponse shape. This is what lets the SignInPage distinguish a
   * 401 (bad creds) from a 403 (needs_verification) without re-implementing
   * status handling in the component: the server's own `error`/`needs_verification`
   * fields are carried through on `err.body`, and we fall back to `err.status`
   * to set `needs_verification` if the body is missing it. A genuine network
   * failure (fetch rejects, no ApiError) is re-thrown so the caller shows the
   * "Network error" copy, matching the magic-link form.
   */
  login: async (email: string, password: string): Promise<AuthLoginResponse> => {
    try {
      return await api.post<AuthLoginResponse>("/api/auth/login", {
        email,
        password,
      });
    } catch (err) {
      if (err instanceof ApiError) {
        const body =
          err.body && typeof err.body === "object"
            ? (err.body as Partial<AuthLoginResponse>)
            : {};
        return {
          ok: false,
          error: body.error ?? err.message,
          needs_verification:
            body.needs_verification ?? err.status === 403,
        };
      }
      throw err;
    }
  },

  /**
   * Create an account. Same THROW-on-non-2xx contract as login: a 409
   * (already exists) and a 400 (validation) both arrive as ApiError and are
   * normalized into AuthSignupResponse so the page reads `error` uniformly.
   */
  /**
   * `plan`/`period` carry the choice made at the pricing page's buy button so
   * the verification email can land the buyer back on it. Optional: a direct
   * signup sends neither. The server whitelists both — passing them through
   * here is not a trust boundary.
   */
  signup: async (
    email: string,
    password: string,
    company: string,
    plan?: string | null,
    period?: string | null,
  ): Promise<AuthSignupResponse> => {
    try {
      return await api.post<AuthSignupResponse>("/api/auth/signup", {
        email,
        password,
        company,
        ...(plan ? { plan } : {}),
        ...(period ? { period } : {}),
      });
    } catch (err) {
      if (err instanceof ApiError) {
        const body =
          err.body && typeof err.body === "object"
            ? (err.body as Partial<AuthSignupResponse>)
            : {};
        return { ok: false, error: body.error ?? err.message, status: err.status };
      }
      throw err;
    }
  },

  /** Always 200 server-side; never reveals whether the account exists.
   * `plan`/`period` are optional and only meaningful right after signup
   * (M2/M4): they keep a buyer's chosen tier alive across a resend, the same
   * way /signup's own activation link already does. */
  resendVerification: (
    email: string,
    plan?: string | null,
    period?: string | null,
  ) =>
    api.post<AuthResendVerificationResponse>("/api/auth/resend-verification", {
      email,
      ...(plan ? { plan } : {}),
      ...(period ? { period } : {}),
    }),

  /**
   * Set (or change) the signed-in operator's password. Authenticated purely by
   * the cspro_session cookie — no email/tenant is sent. Same THROW-on-non-2xx
   * contract as login/signup: a 401 (session expired), 400 (weak password), and
   * 429 (throttled) all arrive as ApiError and are normalized into
   * AuthSetPasswordResponse. We carry `err.status` through so the Settings card
   * can distinguish a 401 (sign in again) from a 400 (fix the password). A
   * genuine network failure (fetch rejects, no ApiError) is re-thrown so the
   * caller shows the "Network error" copy.
   */
  setPassword: async (
    password: string,
    currentPassword?: string,
  ): Promise<AuthSetPasswordResponse> => {
    try {
      return await api.post<AuthSetPasswordResponse>("/api/auth/set-password", {
        password,
        ...(currentPassword ? { current_password: currentPassword } : {}),
      });
    } catch (err) {
      if (err instanceof ApiError) {
        const body =
          err.body && typeof err.body === "object"
            ? (err.body as Partial<AuthSetPasswordResponse>)
            : {};
        return {
          ok: false,
          error: body.error ?? err.message,
          status: err.status,
          ...(typeof body.code === "string" ? { code: body.code } : {}),
        };
      }
      throw err;
    }
  },
};

export const playgroundApi = {
  scenarios: () =>
    api.get<PlaygroundScenariosResponse>("/api/playground/scenarios"),
  usage: () =>
    api.get<PlaygroundUsageResponse>("/api/playground/usage"),
  options: () =>
    api.get<PlaygroundOptionsResponse>("/api/playground/options"),
  run: (scenarioId: string, opts: PlaygroundRunOpts = {}) =>
    api.post<PlaygroundRunResponse>("/api/playground/run", {
      scenario_id: scenarioId,
      ...(opts.tone ? { tone: opts.tone } : {}),
      ...(opts.language ? { language: opts.language } : {}),
    }),
};

export type EvaluationOutcome = "resolved" | "escalated" | "needs_human";

export interface EvaluationScenario {
  id: string;
  name: string;
  customer_prompt: string;
  expected_outcome: EvaluationOutcome;
  allowed_tools: string[];
  forbidden_tools: string[];
  revision: number;
  created_at: string;
  updated_at: string;
}

export type EvaluationScenarioInput = Pick<
  EvaluationScenario,
  "name" | "customer_prompt" | "expected_outcome" | "allowed_tools" | "forbidden_tools"
>;

export interface EvaluationRun {
  id: string;
  scenario_id: string;
  scenario_revision: number;
  deployment_config_revision: number;
  status: "passed" | "failed" | "error";
  observed_outcome: string | null;
  forbidden_tools_called: string[];
  tool_calls: string[];
  reply_excerpt: string | null;
  created_at: string;
  comparison: {
    expected_outcome: string;
    observed_outcome: string | null;
    outcome_match: boolean;
    forbidden_tools_ok: boolean;
    allowed_tools_ok: boolean;
    unexpected_tools: string[];
  } | null;
}

export const evaluationApi = {
  list: (signal?: AbortSignal) =>
    api.get<{ ok: boolean; result: { scenarios: EvaluationScenario[] }; error: string | null }>(
      "/api/evaluations/scenarios", signal,
    ),
  create: (input: EvaluationScenarioInput, signal?: AbortSignal) =>
    api.post<{ ok: boolean; result: { scenario: EvaluationScenario }; error: string | null }>(
      "/api/evaluations/scenarios", input, signal,
    ),
  update: (id: string, input: EvaluationScenarioInput & { expected_revision: number }, signal?: AbortSignal) =>
    api.put<{ ok: boolean; result: { scenario: EvaluationScenario }; error: string | null }>(
      `/api/evaluations/scenarios/${encodeURIComponent(id)}`, input, signal,
    ),
  remove: (id: string, signal?: AbortSignal) =>
    api.del<{ ok: boolean; result: { deleted: true }; error: string | null }>(
      `/api/evaluations/scenarios/${encodeURIComponent(id)}`, signal,
    ),
  runs: (id: string, signal?: AbortSignal) =>
    api.get<{ ok: boolean; result: { runs: EvaluationRun[] }; error: string | null }>(
      `/api/evaluations/scenarios/${encodeURIComponent(id)}/runs`, signal,
    ),
  run: (id: string, expectedRevision: number, signal?: AbortSignal) =>
    api.post<{ ok: boolean; result: { run: EvaluationRun }; error: string | null }>(
      `/api/evaluations/scenarios/${encodeURIComponent(id)}/runs`,
      { expected_revision: expectedRevision }, signal,
    ),
};

export interface BillingUrlResponse {
  ok: boolean;
  url?: string;
  error?: string;
}

export type BillingPlan = "starter" | "growth" | "scale" | "enterprise";
export type BillingPeriod = "monthly" | "annual";

/** How a plan can be acquired right now. Computed server-side from which
 *  Stripe price ids are actually configured, so the console never renders a
 *  buy button for a tier whose checkout would fail. Mirrors `Availability` in
 *  src/billing/plan_catalog.py. */
export type PlanAvailability = "self_serve" | "sales_led" | "unconfigured";

export interface PlanListing {
  plan: BillingPlan;
  label: string;
  tagline: string;
  /** null when the tier is not sold month-to-month (Enterprise). */
  monthly_usd: number | null;
  annual_monthly_usd: number;
  setup_usd: number;
  /** The published price is a floor ("from $7,995"), not a rate. */
  is_from_price: boolean;
  conversations_per_month: number | null;
  brands: number | null;
  seats: number | null;
  integrations: number | null;
  /** Decimal string, e.g. "0.50". One rate across every tier. */
  overage_usd: string;
  availability: Record<BillingPeriod, PlanAvailability>;
}

/** One entitlement line as GET /api/billing/entitlements reports it. */
export interface EntitlementFeature {
  /** Operator-facing name, e.g. "SMS automation". Server-supplied so the
   *  console and the refusal body never word the same thing differently. */
  label: string;
  included: boolean;
  /** The cheapest plan that WOULD carry it — null when already included. */
  included_from: string | null;
}

/** Which of the two sellable services this tenant has -- website support and
 *  the phone receptionist, sold on two separate Stripe subscriptions. `null`
 *  when the plan itself could not be read (see `plan_readable`); the console
 *  must fail open on that the same way it does for `features`. */
export interface EntitlementsServices {
  web_support: {
    active: boolean;
    plan: string;
    /** True once this tenant ever held a paid web subscription, derived
     *  server-side from the RAW (never gate-collapsed) `tenants.plan` column
     *  (F1 fix round 1, m1) -- unlike `plan` above, this survives a
     *  cancellation, so it is the one reliable "ever subscribed" signal.
     *  Absent on a response from before this field existed. */
    ever_subscribed?: boolean;
  };
  receptionist: {
    active: boolean;
    plan: string | null;
    status: string | null;
    included_minutes: number | null;
  };
}

export interface EntitlementsResponse {
  ok: boolean;
  plan?: string;
  /** False when the tenant's plan could not be read. The server's gates fail
   *  OPEN in that case, so the console must not paint locks either. */
  plan_readable?: boolean;
  features?: Record<string, EntitlementFeature>;
  limits?: {
    conversations_per_month: number | null;
    brands: number | null;
    knowledge_collections: number | null;
    integrations: number | null;
    seats: number | null;
  };
  /** sessions: what the retention sweep enforces; null when it could not be read. */
  retention_days?: { sessions: number | null; audit: number };
  regions?: string[];
  services?: EntitlementsServices | null;
  error?: string;
}

export interface BillingPlansResponse {
  ok: boolean;
  plans?: PlanListing[];
  overage_usd?: string;
  contact_url?: string;
  error?: string;
}

export interface BillingStatusResponse {
  ok: boolean;
  plan?: string | null;
  plan_status?: string | null;
  trial_ends_at?: string | null;
  has_billing_account?: boolean;
  error?: string;
}

export interface CacheObservabilitySummary {
  available: boolean;
  request_count: number | null;
  cache_attempted_count: number | null;
  cache_attempted_coverage: number | null;
  realized_hits: number | null;
  realized_writes: number | null;
}

export interface AdminUsageResult {
  tenant_id: string;
  start: string;
  end: string;
  period: Record<string, number>;
  historical_ai: { input_tokens: number; output_tokens: number };
  cache_observability: CacheObservabilitySummary;
  daily: Array<{
    date: string;
    metrics: Record<string, number>;
    historical_ai: { input_tokens: number; output_tokens: number };
  }>;
}

export interface AdminUsageResponse {
  ok: boolean;
  result: AdminUsageResult | null;
  error: string | null;
}

export const usageApi = {
  read: (tenantId: string, start?: string, end?: string, signal?: AbortSignal) => {
    const query = new URLSearchParams();
    if (start) query.set("start", start);
    if (end) query.set("end", end);
    const suffix = query.size ? `?${query.toString()}` : "";
    return api.get<AdminUsageResponse>(
      `/api/v1/admin/tenants/${encodeURIComponent(tenantId)}/usage${suffix}`,
      signal,
    );
  },
};

export interface KbDoc {
  document_id: string;
  title: string;
  source: string;
  version: string;
  content_hash: string;
  content_type: string | null;
}
export interface KbListResponse {
  ok: boolean;
  result?: { tenant_id: string; count: number; documents: KbDoc[] };
  error?: string;
}
export interface KbMutationResponse {
  ok: boolean;
  result?: Record<string, unknown>;
  error?: string;
}

export const kbApi = {
  list: (signal?: AbortSignal) => api.get<KbListResponse>("/api/kb/files", signal),
  remove: (documentId: string) =>
    api.del<KbMutationResponse>(`/api/kb/files/${encodeURIComponent(documentId)}`),
  // Multipart — can't use the JSON helper.
  upload: async (file: File, title: string): Promise<KbMutationResponse> => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title);
    const res = await fetch(`${BASE}/api/kb/files`, {
      method: "POST",
      body: fd,
      credentials: "include",
    });
    const text = await res.text();
    let parsed: unknown = null;
    try {
      parsed = text ? JSON.parse(text) : null;
    } catch {
      /* non-JSON */
    }
    if (!res.ok) {
      const msg =
        parsed && typeof parsed === "object" && "error" in parsed
          ? String((parsed as { error: unknown }).error)
          : `${res.status} ${res.statusText}`;
      throw new ApiError(res.status, msg, parsed);
    }
    return parsed as KbMutationResponse;
  },
};

export interface ProviderInfo {
  provider: string;
  fields: string[];
  // Accepted but not demanded — webhook signing secrets, mostly. Needed only
  // for the INBOUND half of an integration, so requiring them would block
  // outbound-only tenants, but without them a signature can never verify.
  optional_fields?: string[];
  connected: boolean;
  // Connection-health signal (backend): "error" = a dead OAuth token
  // (refresh-failure hook) → "Reconnect needed"; "stale" = cold, verified long
  // ago (nightly sweep) → "Re-verify". Absent/other = healthy. `reason` is a
  // short non-secret phrase for the detail line.
  status?: string | null;
  reason?: string | null;
  // True when the credential is minted by the PLATFORM's OAuth client rather
  // than pasted by the operator — gmail, and only gmail, today. Such a row
  // arrives with an empty `fields` list and `POST /connect` refuses it
  // ("gmail connects through Authorize"), so a drawer that renders inputs for
  // it collects nothing and then posts into a 400. Optional here, not because
  // the route can omit it (it always sends the key), but because a client
  // deployed ahead of that route must read a missing key as "not OAuth".
  oauth?: boolean;
  // Where the operator builds the connected app whose id/secret this row's
  // fields want — salesforce and zoho; explicitly null for every other
  // provider. Not a credential and not tenant-specific: it is the vendor's own
  // documentation URL, chosen server-side so one list is the only list.
  docs_url?: string | null;
}
export interface ProvidersListResponse {
  ok: boolean;
  result?: { providers: ProviderInfo[] };
  error?: string;
}
// `GET /api/providers/oauth/{provider}/authorize` — the consent URL, and
// nothing else. The signed `state` and the tenant id the server builds it from
// are deliberately dropped before the response is written, so there is no
// second field here to be tempted by. A refusal answers `ok: false` with an
// `error` sentence and NO `result` (400 "provider does not authorize through
// OAuth" / "authorization is not configured"), which is why `result` is
// optional and the caller must check both.
export interface ProviderOauthAuthorizeResponse {
  ok: boolean;
  result?: { redirect_url: string };
  error?: string | null;
}
export interface ProviderMutationResponse {
  ok: boolean;
  // `status` is the lifecycle word the twilio_byo connect route answers with.
  // Authorizing an account is NOT the same as having a working transport, so
  // that route can answer `connected: false` with `status: "authorized"` and
  // the caller must not congratulate the operator on a connection they do not
  // have yet. Every other provider omits it.
  result?: { provider: string; connected: boolean; status?: string | null };
  error?: string;
}

// --- Bring-your-own Twilio (SMS transport) ----------------------------------
//
// Four states in order -- the account is not authorized; it is authorized but
// no number is proven; a number passed proof in both directions; SMS actually
// sends from it -- plus `revoked`, which is off that line entirely: the
// connection's secret is gone. Omitting `revoked` made the rail treat a dead
// authorization as "choose a number" and then fail on the inventory read.
// `connected` is deliberately separate from `status` -- see the note on
// ProviderMutationResponse.
//
// GET /api/providers reports these on the `twilio_byo` row too
// (`_overlay_twilio_byo_lifecycle_status` in src/providers/routes.py), which is
// what lets the Providers panel know a setup is unfinished.
export type TwilioByoStatus =
  "not_configured" | "authorized" | "resource_ready" | "active" | "revoked";
export interface TwilioByoStatusResponse { ok: boolean; result: { status: TwilioByoStatus; connected: boolean }; error: string | null }
export interface TwilioByoInventoryItem { account_sid: string; sid: string; phone_number: string; sms: boolean; messaging_service_sids: string[]; iso_country: string; tfv_status: string | null; a2p_status: string | null; restricted_key_healthy: boolean }
export interface TwilioByoInventoryResponse { ok: boolean; result: { resources: TwilioByoInventoryItem[] }; error: string | null }
export interface TwilioByoPrepareResponse { ok: boolean; result: { status: string; sender_sid: string; sender_e164: string; proof_nonce: string; credential_revision: number; connected: false }; error: string | null }
export interface TwilioByoFinalizeResponse { ok: boolean; result: { status: string; connected: boolean }; error: string | null }
export interface TwilioActivateResponse { ok: boolean; result: Record<string, unknown>; error: string | null }

export interface ProviderVerifyResponse {
  ok: boolean;
  result?: { provider: string; verified: boolean; detail: string; checked_at: string };
  error?: string;
}

export interface EmailEnableResponse {
  ok: boolean;
  result?: { slug: string; address: string };
  error?: string;
}

export const providersApi = {
  list: () => api.get<ProvidersListResponse>("/api/providers"),
  connect: (provider: string, credentials: Record<string, string>) =>
    api.post<ProviderMutationResponse>(`/api/providers/${encodeURIComponent(provider)}/connect`, { credentials }),
  disconnect: (provider: string) =>
    api.del<ProviderMutationResponse>(`/api/providers/${encodeURIComponent(provider)}`),
  verify: (provider: string) =>
    api.post<ProviderVerifyResponse>(`/api/providers/${encodeURIComponent(provider)}/verify`),
  /* The session-authed half of the OAuth pair. It returns the consent URL as
     JSON rather than a 302 on purpose: a redirect out of an API route is
     followed by `fetch`, which would drag Google's consent page into an XHR
     and show the operator nothing. The caller navigates. `oauth` is a literal
     path segment registered ABOVE every `/{provider}` route server-side, so it
     can never be read as a provider slug. */
  oauthAuthorize: (provider: string) =>
    api.get<ProviderOauthAuthorizeResponse>(
      `/api/providers/oauth/${encodeURIComponent(provider)}/authorize`,
    ),
  // Mints (or idempotently re-reads) the tenant's inbound-email ingest slug.
  enableEmail: (expectedRevision: number) =>
    api.post<EmailEnableResponse>("/api/providers/email/enable", {
      expected_revision: expectedRevision,
    }),

  // --- Bring-your-own Twilio --------------------------------------------
  // All five are owner-gated server-side (403 `owner_required`); the panel
  // renders that as a sentence rather than hiding the rail, because an
  // operator who cannot do it still needs to know who can.
  twilioByoStatus: (signal?: AbortSignal) =>
    api.get<TwilioByoStatusResponse>("/api/providers/twilio/byo/status", signal),
  twilioByoInventory: (signal?: AbortSignal) =>
    api.get<TwilioByoInventoryResponse>("/api/providers/twilio/byo/inventory", signal),
  // Sends the outbound proof text. The response's `credential_revision` is the
  // one the matching finalize MUST echo -- a re-authorization between the two
  // calls has to fail loudly, not silently prove a number under a new key.
  twilioByoPrepare: (senderSid: string, testTo: string) =>
    api.post<TwilioByoPrepareResponse>("/api/providers/twilio/byo/resources", {
      sender_sid: senderSid, test_to: testTo,
    }),
  twilioByoFinalize: (senderSid: string, testTo: string, credentialRevision: number) =>
    api.post<TwilioByoFinalizeResponse>("/api/providers/twilio/byo/resources/finalize", {
      sender_sid: senderSid, test_to: testTo, credential_revision: credentialRevision,
    }),
  twilioActivate: (mode: "managed" | "byo", resourceId: string, expectedRevision: number) =>
    api.post<TwilioActivateResponse>("/api/providers/twilio/activate", {
      provider_mode: mode, provider_resource_id: resourceId,
      expected_revision: expectedRevision,
    }),
};

// Tier-0 channel status (web/email/sms) — operator badges, read-only.
export interface ChannelStatus {
  web: { status: "live" };
  email: { status: "live" | "not_connected"; address?: string | null };
  sms: {
    status: "unverified" | "provision_requested" | "pending" | "verified" | "failed";
    channel_enabled: boolean;
    toll_free_number: string | null;
    submitted_at: string | null;
    reason?: string | null;
  };
}
export interface ChannelStatusResponse {
  ok: boolean;
  result?: { channels: ChannelStatus };
  error?: string | null;
}

export type UnifiedChannelId =
  | "web" | "email" | "sms" | "whatsapp"
  | "voice_inbound" | "voice_outbound";

export type WhatsAppProviderMode = "managed" | "byo";
export type VoiceProviderMode = "managed" | "byo";

export interface VoiceChannelSummary {
  direction: "inbound" | "outbound";
  provider_mode: "none" | "native" | VoiceProviderMode;
  ready: boolean;
  blockers: string[];
  resource_status: string | null;
  voice_capable: boolean;
  url_readback_verified: boolean;
  recording_enabled: boolean;
  compliance_status: string | null;
  credential_revision: number | null;
}

export interface VoiceInventoryItem {
  resource_id: string;
  display: string;
  voice_capable: boolean;
  eligible: boolean;
}

export interface VoiceInventoryResponse {
  ok: boolean;
  result?: {
    provider_mode: VoiceProviderMode;
    items: VoiceInventoryItem[];
    inventory_revision: string;
    provider_revision: number;
    credential_revision: number;
    voice_inbound_revision: number;
    voice_outbound_revision: number;
  };
  error?: string | null;
}

export interface VoiceProvisionResponse {
  ok: boolean;
  result?: { ready: boolean; resource_count: number; operation_id?: string; state?: string };
  error?: string | null;
}

export interface VoiceConfigurationTestResponse {
  ok: boolean;
  result?: { ok: boolean; checks: Array<{ name: string; ok: boolean; blocker: string }>; blockers: string[]; live_call: false };
  error?: string | null;
}
export interface WhatsAppSenderInventoryItem {
  resource_id: string;
  status: string;
  sender_address: string | null;
  authorization_status: string | null;
  quality_status: string | null;
  compliance_status: string | null;
  selected: boolean;
}
export interface WhatsAppChannelSummary {
  provider_account_label: string;
  waba_status: "authorized" | "pending" | "rejected" | "missing";
  selected_sender: WhatsAppSenderInventoryItem | null;
  template_counts: { approved: number; pending: number; rejected: number };
  templates_last_synced_at: string | null;
  webhook_status: "verified" | "missing" | "failed" | "reconcile_required";
  receipt_writer_status: "verified" | "missing" | "failed" | "reconcile_required";
  ready_to_activate: boolean;
}

/* Managed toll-free provisioning, as the channel list now projects it. Four
   real stages run while `toll_free_verification_status` sits on
   `provision_requested` the whole time, so the ladder -- not the status -- is
   what says where a tenant actually is. `reason` is already sanitized server
   side; `blocker` is the one code that means the failure is ours. BYO carries
   no ladder: its progress is the Twilio connection under Advanced Providers. */
export interface SmsControlSummary {
  provider_mode: string;
  status: string;
  stage: string;
  stages: { key: string; label: string; state: "done" | "current" | "pending" | "failed" }[];
  reason: string | null;
  blocker: string | null;
}

/* Moved from app/src/components/ChannelsPanel.tsx (Task 10, batch-e): the
   shape the quiet-hours editor composes lives beside every other channel
   wire type now that this file is no longer T9's alone. The index signature
   is what keeps `{ ...channel.policy }` lossless: the editor edits three
   keys and must carry every other one through untouched, because the server
   REPLACES policy_json rather than merging it. */
export interface ChannelPolicy {
  quiet_hours?: boolean;
  timezone?: string | null;
  business_hours?: Record<string, Array<{ start: string; end: string }>>;
  max_delivery_attempts?: number;
  retry_delay_seconds?: number;
  /** What the AI says when it answers a call. Required before voice is live. */
  voice_prompt?: string;
  /** Read to every caller before the AI speaks. Tenant-authored, never ours. */
  ai_disclosure?: string;
  [k: string]: unknown;
}

export interface ChannelControl {
  channel: UnifiedChannelId;
  desired_state: "on" | "off";
  effective_state: "on" | "off" | "degraded" | "blocked";
  setup_state: "not_configured" | "configuring" | "pending_compliance" | "ready" | "failed";
  provider_mode: "none" | "native" | "managed" | "byo";
  ai_enabled: boolean;
  inbound_enabled: boolean;
  outbound_enabled: boolean;
  policy: ChannelPolicy;
  fallback: {
    action?: "handoff" | "queue" | "disable";
    after_hours_action?: "handoff" | "queue" | "disable";
    message?: string;
    target?: "operator" | "inbox";
    safe_mode?: "ivr" | "callback" | "human_only";
  };
  revision: number;
  public_address: string | null;
  gate_reasons: string[];
  health: { status: "healthy" | "degraded" | "blocked" | "off"; monitoring: string };
  whatsapp?: WhatsAppChannelSummary;
  voice?: VoiceChannelSummary;
  sms?: SmsControlSummary;
}

export interface ChannelsControlResponse {
  ok: boolean;
  result?: { channels: ChannelControl[]; can_manage: boolean };
  error?: string | null;
}

export interface ChannelUpdateResponse {
  ok: boolean;
  result?: { channel: ChannelControl };
  error?: string | null;
}

export interface ChannelReadinessResponse {
  ok: boolean;
  result?: {
    channel: UnifiedChannelId;
    status: "passed" | "blocked" | "degraded" | "off";
    gate_reasons: string[];
    revision: number;
  };
  error?: string | null;
}

export interface ChannelAuditItem {
  ts: string | null;
  event: string;
  actor: string;
  channel: UnifiedChannelId;
  details: { changed_fields?: string[]; transitions?: Record<string, unknown>; revision?: number };
}

export interface ChannelAuditResponse {
  ok: boolean;
  result?: { total: number; items: ChannelAuditItem[] };
  error?: string | null;
}

export interface TollFreeVerifyResponse {
  ok: boolean;
  result?: { status: "pending" | "verified" | "failed"; detail?: string };
  error?: string;
}

export const channelsApi = {
  // Compatibility status used by existing Overview badges.
  list: () => api.get<ChannelStatusResponse>("/api/providers/channels"),
  controlList: (signal?: AbortSignal) => api.get<ChannelsControlResponse>("/api/channels", signal),
  update: (channel: UnifiedChannelId, expectedRevision: number, patch: Partial<ChannelControl>) =>
    api.patch<ChannelUpdateResponse>(`/api/channels/${encodeURIComponent(channel)}`, {
      expected_revision: expectedRevision, patch,
    }),
  readiness: (channel: UnifiedChannelId) =>
    api.post<ChannelReadinessResponse>(`/api/channels/${encodeURIComponent(channel)}/readiness`),
  audit: (channel?: UnifiedChannelId, limit = 20) => {
    const query = new URLSearchParams({ limit: String(limit) });
    if (channel) query.set("channel", channel);
    return api.get<ChannelAuditResponse>(`/api/channels/audit?${query.toString()}`);
  },
  whatsappInventory: (providerMode: WhatsAppProviderMode, signal?: AbortSignal) => {
    const query = new URLSearchParams({ provider_mode: providerMode });
    return api.get<WhatsAppInventoryResponse>(
      `/api/channels/whatsapp/inventory?${query.toString()}`,
      signal,
    );
  },
  refreshWhatsApp: (
    providerMode: WhatsAppProviderMode, expectedRevision: number,
    signal?: AbortSignal,
  ) => api.post<WhatsAppMutationResponse>(
    "/api/channels/whatsapp/inventory/refresh",
    { provider_mode: providerMode, expected_revision: expectedRevision },
    signal,
  ),
  selectWhatsAppSender: (
    providerMode: WhatsAppProviderMode, resourceId: string,
    expectedRevision: number, signal?: AbortSignal,
  ) => api.post<WhatsAppMutationResponse>(
    "/api/channels/whatsapp/sender/select",
    {
      provider_mode: providerMode, resource_id: resourceId,
      expected_revision: expectedRevision,
    },
    signal,
  ),
  syncWhatsAppTemplates: (
    providerMode: WhatsAppProviderMode, expectedRevision: number,
    signal?: AbortSignal,
  ) => api.post<WhatsAppMutationResponse>(
    "/api/channels/whatsapp/templates/sync",
    { provider_mode: providerMode, expected_revision: expectedRevision },
    signal,
  ),
  setupWhatsAppWebhook: (idempotencyKey: string, signal?: AbortSignal) =>
    api.post<WhatsAppWebhookSetupResponse>(
      "/api/channels/whatsapp/webhook/setup",
      { idempotency_key: idempotencyKey },
      signal,
    ),
  activateWhatsApp: (
    providerMode: WhatsAppProviderMode, expectedRevision: number,
    signal?: AbortSignal,
  ) => api.post<WhatsAppMutationResponse>(
    "/api/channels/whatsapp/activate",
    { provider_mode: providerMode, expected_revision: expectedRevision },
    signal,
  ),
  voiceInventory: (providerMode: VoiceProviderMode, signal?: AbortSignal) => {
    const query = new URLSearchParams({ provider_mode: providerMode });
    return api.get<VoiceInventoryResponse>(
      `/api/channels/voice/inventory?${query.toString()}`,
      signal,
    );
  },
  voiceProvision: (
    request: {
      provider_mode: VoiceProviderMode;
      operation_id: string;
      selected_resource_id: string;
      inventory_revision: string;
      expected_provider_revision: number;
      expected_credential_revision: number;
      expected_inbound_revision: number;
      expected_outbound_revision: number;
    },
    signal?: AbortSignal,
  ) => api.post<VoiceProvisionResponse>(
    "/api/channels/voice/provision", request, signal,
  ),
  voiceTest: (providerMode: VoiceProviderMode, signal?: AbortSignal) =>
    api.post<VoiceConfigurationTestResponse>(
      "/api/channels/voice/test",
      { provider_mode: providerMode },
      signal,
    ),
  verifyTollFree: (body: Record<string, unknown>) =>
    api.post<TollFreeVerifyResponse>("/api/providers/twilio/verify-toll-free", body),
  // The off switch for the address `providersApi.enableEmail` minted. Same
  // optimistic-revision contract as enable, so the same 400/409 answers apply.
  disableEmail: (expectedRevision: number) =>
    api.post<{ ok: boolean; result?: { slug: string | null; enabled: false }; error?: string | null }>(
      "/api/providers/email/disable",
      { expected_revision: expectedRevision },
    ),
};

export interface TwilioTransferResult {
  ok: boolean;
  status?: string;
  state?: string;
  error?: string;
  transfer_id?: string;
  revision?: number;
  pending?: boolean;
  safe_step_code?: string | null;
  remediation_code?: string | null;
  disclosure_version?: string;
  disclosure?: {
    version: string;
    effect_codes: string[];
    requires_affirmative_owner_acknowledgment: boolean;
  };
  summary?: {
    phone_number_count: number;
    phone_number_displays?: string[];
    messaging_service_count: number;
    compliance_reference_count?: number;
    unsupported_resource_count?: number;
  };
  blocking_step_codes?: string[];
  completed_step_codes?: string[];
  rollback_instruction_codes?: string[];
  progress?: { completed_step_count: number };
  history?: Array<{ code: string; at: string }>;
  confirmation_token?: string;
  retry_confirmation_token?: string;
}

export interface WhatsAppInventoryResponse {
  ok: boolean;
  result?: { provider_mode: WhatsAppProviderMode; items: WhatsAppSenderInventoryItem[] };
  error?: string | null;
}
export interface WhatsAppMutationResponse {
  ok: boolean;
  result?: {
    provider_mode?: WhatsAppProviderMode;
    revision?: number;
    resource_count?: number;
    template_count?: number;
    desired_state?: string;
    effective_state?: string;
    setup_state?: string;
    gate_reasons?: string[];
  };
  error?: string | null;
}
export interface WhatsAppWebhookSetupResponse {
  ok: boolean;
  result?: {
    operation_id?: string;
    state: string;
    ready: boolean;
    webhook_status?: string;
    receipt_writer_status?: string;
    inventory_revision: number;
  };
  error?: string | null;
}

export interface TwilioTransferResponse {
  ok: boolean;
  result?: TwilioTransferResult;
  error?: string | null;
}

const transferPath = (transferId: string) =>
  `/api/channels/twilio-transfer/${encodeURIComponent(transferId)}`;

export const twilioTransferApi = {
  preflight: () =>
    api.post<TwilioTransferResponse>("/api/channels/twilio-transfer/preflight"),
  confirm: (transferId: string, disclosureVersion: string, acknowledged: boolean) =>
    api.post<TwilioTransferResponse>(`${transferPath(transferId)}/confirm`, {
      disclosure_version: disclosureVersion, acknowledged,
    }),
  execute: (transferId: string, confirmationToken: string) =>
    api.post<TwilioTransferResponse>(`${transferPath(transferId)}/execute`, {
      confirmation_token: confirmationToken,
    }),
  resume: (transferId: string, expectedRevision: number) =>
    api.post<TwilioTransferResponse>(`${transferPath(transferId)}/resume`, {
      expected_revision: expectedRevision,
    }),
  confirmRetry: (transferId: string, expectedRevision: number, acknowledged: boolean) =>
    api.post<TwilioTransferResponse>(`${transferPath(transferId)}/confirm-retry`, {
      expected_revision: expectedRevision, acknowledged,
    }),
  retry: (transferId: string, retryConfirmationToken: string) =>
    api.post<TwilioTransferResponse>(`${transferPath(transferId)}/retry`, {
      retry_confirmation_token: retryConfirmationToken,
    }),
  status: (transferId: string) =>
    api.get<TwilioTransferResponse>(transferPath(transferId)),
};

export type KnownInboxChannel =
  | "web" | "email" | "sms" | "whatsapp"
  | "voice_inbound" | "voice_outbound";
export type InboxChannel = KnownInboxChannel | (string & Record<never, never>);

export interface VoiceHandoffSummary {
  resolution_status: string;
  risk_flag: string;
  turn_count: number;
  last_customer_message: string;
}
export interface VoiceHandoffCommitment {
  type: string;
  status: string;
  detail?: string;
}
export interface VoiceHandoffPacket {
  handoff_id: string;
  reason: string;
  status: string;
  summary: VoiceHandoffSummary;
  commitments: VoiceHandoffCommitment[];
  transfer_phase?: string | null;
  transfer_outcome?: string | null;
}

export interface InboxSessionSummary {
  session_id: string;
  status: string | null;
  snoozed_until: string | null;
  takeover_status: string | null;
  taken_over_by: string | null;
  customer: { name?: string; email?: string } & Record<string, unknown>;
  preview: string | null;
  turn_count: number;
  updated_at: string | null;
  // Canonical transport; absent/legacy sessions are projected as "web".
  channel: InboxChannel;
  // Wave-1 reliability: true when an escalation's ticket/Slack delivery failed
  // silently and a human must follow up manually. Server defaults to false.
  escalation_delivery_failed?: boolean;
  // How many turns the visitor themselves contributed. 0 = the widget opened
  // and nobody ever typed, which is the common case for a busy marketing page.
  customer_turn_count?: number;
}
export interface InboxListResponse {
  ok: boolean;
  // `next_cursor` is an opaque keyset cursor for Load-more; null/absent = last
  // page. It carries only (updated_at, session_id) — never a tenant id.
  result?: {
    sessions: InboxSessionSummary[];
    next_cursor?: string | null;
    // True when the server filtered out sessions nobody wrote in, so the list
    // can say so rather than looking mysteriously short.
    silent_hidden?: boolean;
  };
  error?: string;
}
export interface InboxTurn {
  role: "customer" | "user" | "assistant" | "operator" | "system";
  content: string;
  ts?: string;
  operator?: string;
  // Sub-project D: a customer turn may carry an uploaded file. Bytes are served
  // operator-cookie-authed at GET /api/attachments/{id} (inline, nosniff).
  attachment?: {
    id: string;
    filename: string;
    content_type: string;
    size_bytes: number;
  };
  // Verified inbound-provider metadata only. Provider URLs are never projected.
  media?: Array<{ kind: string; content_type: string; label: string }>;
}
export interface ConversationInsights {
  intent: string | null;
  resolution_status: string | null;
  sentiment: number | null; // -1..1
  ai_confidence: number | null; // 0..1
  language: string | null;
  escalation_count: number;
  ticket_id: string | null;
  risk_flag: string | null;
  csat_score: number | null;
  csat_comment: string | null;
  turn_count: number;
  customer_turns: number;
  created_at: string | null;
  updated_at: string | null;
}
// A refund the assistant proposed that's awaiting an operator's approve/deny decision.
// charge_id/amount/reason are display-safe (no card PII). amount_cents null =
// full refund.
export interface PendingRefund {
  charge_id: string;
  amount_cents: number | null;
  currency: string;
  reason: string | null;
  requested_at: string | null;
}
export interface InboxTranscriptResponse {
  ok: boolean;
  result?: {
    session_id: string;
    turns: InboxTurn[];
    takeover_status: string | null;
    taken_over_by: string | null;
    customer: Record<string, unknown>;
    // Canonical tenant-scoped customer reference. Null for legacy/unlinked
    // sessions; never a protected channel identifier.
    customer_id?: string | null;
    resolution_status: string | null;
    status: string | null;
    snoozed_until: string | null;
    // Contract exclusions an operator has declared on this conversation. They
    // remove it from the Verified Tier-One Resolution denominator; they do not
    // change what the tenant is billed.
    is_spam?: boolean;
    is_test?: boolean;
    insights?: ConversationInsights;
    // Same channel signal as InboxSessionSummary — "web" | "email".
    channel: InboxChannel;
    // Wave-1 reliability: silently-failed escalation delivery + which legs
    // failed (["ticket"], ["slack"], or both). PII-free.
    escalation_delivery_failed?: boolean;
    escalation_delivery_failed_channels?: string[];
    // Operator-approved refund flow: the pending proposal or null.
    pending_refund?: PendingRefund | null;
    // Shadow mode: the reply the assistant drafted and did NOT send. Only ever present
    // on this operator-authenticated endpoint — the widget's own response
    // carries no reply text at all for a shadow turn.
    shadow_draft?: ShadowDraft | null;
    // Voice warm-transfer context is a bounded, text-only public projection.
    handoff?: VoiceHandoffPacket | null;
  };
  error?: string;
}
/** A reply the assistant wrote that nobody has sent. */
export interface ShadowDraft {
  text: string;
  drafted_at: string;
  /** How many turns the conversation held when it was written — a draft older
   *  than the transcript is answering a message that has moved on. */
  turn_count?: number;
}
export interface DiscardDraftResponse {
  ok: boolean;
  result?: { discarded: boolean; shadow_draft: null };
  error?: string;
}
export interface RefundDecisionResponse {
  ok: boolean;
  result?: { decision: "approved" | "denied"; refund_id?: string | null; pending_refund: null };
  error?: string;
}
export interface InboxMutationResponse {
  ok: boolean;
  result?: Record<string, unknown>;
  error?: string;
}
export type OperatorDeliveryStatus =
  | "pending" | "sending" | "sent" | "delivered"
  | "failed" | "retry" | "cancelled";
export interface OperatorDelivery {
  delivery_id: string;
  status: OperatorDeliveryStatus;
  attempts: number;
  retryable: boolean;
  sanitized_failure: string | null;
  next_attempt_at: string | null;
}
export interface InboxSendResponse {
  ok: boolean;
  result?: { turn: InboxTurn; turn_count: number; delivery: OperatorDelivery };
  error?: string | null;
}
export interface InboxDeliveryResponse {
  ok: boolean;
  result?: { delivery: OperatorDelivery };
  error?: string | null;
}
export interface InboxResolveResponse {
  ok: boolean;
  result?: { resolution_status: string };
  error?: string;
}
export type SessionDisposition = "spam" | "test";
/**
 * Two of the five Verified Tier-One Resolution exclusions in the Terms are
 * human judgements — a test conversation and a real one look identical in the
 * transcript. This is the operator's answer. It removes the conversation from
 * the guarantee's denominator; it does not change what the tenant is billed.
 */
export interface InboxDispositionResponse {
  ok: boolean;
  result?: {
    session_id: string;
    disposition: SessionDisposition;
    excluded: boolean;
    is_spam: boolean;
    is_test: boolean;
    marked_by: string;
    marked_at: string;
  };
  error?: string;
}
export interface InboxDeleteResponse {
  ok: boolean;
  result?: { deleted: boolean };
  error?: string;
}
export interface InboxClearResolvedResponse {
  ok: boolean;
  result?: {
    deleted: number;
    /* Whether anything was left behind. `deleted` alone cannot tell a finished
       job from one that stopped at its page cap, and for a delete nobody can
       undo that difference is the whole story. */
    complete?: boolean;
  };
  error?: string;
}

export interface InboxResolvedCountResponse {
  ok: boolean;
  result?: {
    count: number;
    /* The count stopped at the page cap, so render it as "1,000+" rather than
       implying every one was counted. */
    at_least: boolean;
  };
  error?: string;
}

export interface InboxNote {
  id: string;
  author: string;
  text: string;
  created_at: string;
}
export interface InboxNoteListResponse {
  ok: boolean;
  result?: { notes: InboxNote[] };
  error?: string;
}
export interface InboxNoteCreateResponse {
  ok: boolean;
  result?: { note: InboxNote };
  error?: string;
}
export interface InboxSnoozeResponse {
  ok: boolean;
  result?: { session_id: string; snoozed_until: string | null };
  error?: string;
}
export type InboxBatchAction =
  | "resolve"
  | "reopen"
  | "snooze"
  | "delete"
  | "takeover"
  | "handback"
  // Contract exclusions, in bulk — a spam flood is what one-at-a-time marking
  // handles worst. There is no batch "clear" counterpart: undoing a mistaken
  // sweep is a per-conversation correction.
  | "mark_spam"
  | "mark_test";
export interface InboxBatchRequest {
  action: InboxBatchAction;
  session_ids: string[];
  snoozed_until?: string | null;
}
export interface InboxBatchItem {
  session_id: string;
  ok: boolean;
  result?: Record<string, unknown>;
  error?: string;
}
export interface InboxBatchResponse {
  ok: boolean;
  result?: {
    action: InboxBatchAction;
    results: InboxBatchItem[];
    succeeded: number;
    failed: number;
  };
  error?: string;
}

// ---------------------------------------------------------------------------
// Operator saved views — named, persisted inbox filter sets (status + takeover
// + text query) the operator switches to like a preset tab. Evaluated
// client-side over the already-fetched session list; the backend only stores
// the definitions (per-tenant via the session cookie). Mirrors the backend
// enums so the editor can validate + label before save.
// ---------------------------------------------------------------------------
export type SavedViewStatus = "any" | "open" | "escalated" | "needs_human" | "resolved";
export type SavedViewTakeover = "any" | "human" | "ai";

export interface SavedViewFilters {
  status: SavedViewStatus;
  takeover: SavedViewTakeover;
  query: string;
}
export interface SavedView {
  id: string;
  name: string;
  filters: SavedViewFilters;
  position: number;
}

/** Body for create/update — server applies its own defaults for omitted optionals. */
export interface SavedViewInput {
  name: string;
  filters: SavedViewFilters;
  position?: number;
}

export interface SavedViewListResponse {
  ok: boolean;
  result?: { views: SavedView[] };
  error?: string | null;
}
export interface SavedViewResponse {
  ok: boolean;
  result?: { view: SavedView };
  error?: string | null;
}
export interface SavedViewDeleteResponse {
  ok: boolean;
  error?: string | null;
}

export const savedViewsApi = {
  list: () => api.get<SavedViewListResponse>("/api/inbox/views"),
  create: (body: SavedViewInput) =>
    api.post<SavedViewResponse>("/api/inbox/views", body),
  update: (id: string, body: SavedViewInput) =>
    api.put<SavedViewResponse>(
      `/api/inbox/views/${encodeURIComponent(id)}`,
      body,
    ),
  remove: (id: string) =>
    api.del<SavedViewDeleteResponse>(
      `/api/inbox/views/${encodeURIComponent(id)}`,
    ),
};

export interface SuggestReplyResponse {
  ok: boolean;
  result?: { draft: string };
  error?: string | null;
}

export const inboxApi = {
  list: (
    status?: string,
    cursor?: string,
    signal?: AbortSignal,
    scope: "active" | "snoozed" | "all" = "active",
    // The widget opens a session the moment it loads, so most rows belong to
    // visitors who never typed anything. The server hides those unless asked;
    // the inbox's "Include visitors who never wrote" toggle is what asks.
    includeSilent = false,
  ) => {
    const qs = new URLSearchParams();
    if (status) qs.set("status", status);
    if (cursor) qs.set("cursor", cursor);
    qs.set("scope", scope);
    if (includeSilent) qs.set("include_silent", "true");
    const suffix = qs.toString();
    return api.get<InboxListResponse>(
      `/api/inbox/sessions${suffix ? `?${suffix}` : ""}`,
      signal,
    );
  },
  transcript: (sessionId: string, signal?: AbortSignal) =>
    api.get<InboxTranscriptResponse>(
      `/api/inbox/sessions/${encodeURIComponent(sessionId)}`,
      signal,
    ),
  notes: (sessionId: string, signal?: AbortSignal) =>
    api.get<InboxNoteListResponse>(
      "/api/inbox/sessions/" + encodeURIComponent(sessionId) + "/notes",
      signal,
    ),
  addNote: (sessionId: string, text: string) =>
    api.post<InboxNoteCreateResponse>(
      "/api/inbox/sessions/" + encodeURIComponent(sessionId) + "/notes",
      { text },
    ),
  snooze: (sessionId: string, until: string | null) =>
    api.put<InboxSnoozeResponse>(
      "/api/inbox/sessions/" + encodeURIComponent(sessionId) + "/snooze",
      { until },
    ),
  batch: (body: InboxBatchRequest) =>
    api.post<InboxBatchResponse>("/api/inbox/sessions/batch", body),
  takeover: (sessionId: string, signal?: AbortSignal) =>
    api.post<InboxMutationResponse>(
      `/api/inbox/sessions/${encodeURIComponent(sessionId)}/takeover`,
      undefined,
      signal,
    ),
  handback: (sessionId: string, signal?: AbortSignal) =>
    api.post<InboxMutationResponse>(
      `/api/inbox/sessions/${encodeURIComponent(sessionId)}/handback`,
      undefined,
      signal,
    ),
  /** `fromDraft` is the ✨ Suggest draft this reply started from, when there
   *  was one. Sent back so the server can record the operator's edit — the
   *  suggest endpoint is read-only and cannot stash it itself. */
  send: (sessionId: string, message: string, fromDraft?: string | null, signal?: AbortSignal) =>
    api.post<InboxSendResponse>(
      `/api/inbox/sessions/${encodeURIComponent(sessionId)}/messages`,
      fromDraft ? { message, from_draft: fromDraft } : { message },
      signal,
    ),
  retryDelivery: (deliveryId: string, signal?: AbortSignal) =>
    api.post<InboxDeliveryResponse>(
      `/api/inbox/deliveries/${encodeURIComponent(deliveryId)}/retry`,
      undefined,
      signal,
    ),
  // Ask the assistant's engine for a proposed reply to the latest customer message.
  // Returns a draft only — does NOT send. Operator edits then sends via send().
  suggestReply: (sessionId: string) =>
    api.post<SuggestReplyResponse>(
      `/api/inbox/sessions/${encodeURIComponent(sessionId)}/suggest-reply`,
    ),
  // Operator approves/denies the session's pending refund. approve=true issues
  // the refund via Stripe; approve=false declines it. Both clear the pending.
  refundApproval: (sessionId: string, approve: boolean) =>
    api.post<RefundDecisionResponse>(
      `/api/inbox/sessions/${encodeURIComponent(sessionId)}/refund-approval`,
      { approve },
    ),
  // Throw away an unsent shadow-mode draft. Sending it instead goes through
  // send(), which clears the draft on its way past. 409 when there is nothing
  // to discard — usually because a colleague already dealt with it.
  discardDraft: (sessionId: string) =>
    api.post<DiscardDraftResponse>(
      `/api/inbox/sessions/${encodeURIComponent(sessionId)}/discard-draft`,
    ),
  resolve: (sessionId: string) =>
    api.post<InboxResolveResponse>(
      `/api/inbox/sessions/${encodeURIComponent(sessionId)}/resolve`,
    ),
  reopen: (sessionId: string) =>
    api.post<InboxResolveResponse>(
      `/api/inbox/sessions/${encodeURIComponent(sessionId)}/reopen`,
    ),
  setDisposition: (
    sessionId: string, disposition: SessionDisposition, excluded: boolean,
  ) =>
    api.post<InboxDispositionResponse>(
      `/api/inbox/sessions/${encodeURIComponent(sessionId)}/disposition`,
      { disposition, excluded },
    ),
  remove: (sessionId: string) =>
    api.del<InboxDeleteResponse>(
      `/api/inbox/sessions/${encodeURIComponent(sessionId)}`,
    ),
  resolvedCount: (signal?: AbortSignal) =>
    api.get<InboxResolvedCountResponse>(
      "/api/inbox/sessions/resolved-count", signal,
    ),
  clearResolved: () =>
    api.post<InboxClearResolvedResponse>("/api/inbox/sessions/clear-resolved"),
};

export type MonitoringOperator =
  | "sentiment" | "compliance" | "escalation" | "churn"
  | "fraud_security" | "low_confidence" | "repeated_failure" | "tenant_defined";
export type MonitoringSeverity = "info" | "warning" | "critical";
export type MonitoringAction =
  | "annotate" | "notify" | "highlight" | "pause_ai" | "require_approval" | "handoff";
export type MonitoringAlertStatus = "open" | "acknowledged" | "resolved";

export interface MonitoringAlert {
  cursor: number;
  signal: string;
  session_id: string;
  channel: KnownInboxChannel;
  source_range: { start_event_id: string; end_event_id: string };
  rule: { id: string; revision: number };
  operator: MonitoringOperator;
  severity: MonitoringSeverity;
  action: MonitoringAction;
  actions: MonitoringAction[];
  status: MonitoringAlertStatus;
  revision: number;
  timestamps: {
    source_received_at: string;
    monitoring_persisted_at: string;
    alert_created_at: string;
    alert_updated_at: string;
  };
  receive_to_persist_ms?: number | null;
}

export interface MonitoringAlertsResponse {
  ok: boolean;
  result?: { alerts: MonitoringAlert[]; next_cursor: number };
  error?: string | null;
}

export interface MonitoringAlertMutationResponse {
  ok: boolean;
  result?: {
    alert: { signal: string; status: MonitoringAlertStatus; revision: number };
    handoff?: { handoff_id: string; status: string; notification_status: string };
  };
  error?: string | null;
}

export interface MonitoringRuleAuthority {
  severity: MonitoringSeverity;
  confidence_threshold: number;
  final_only: boolean;
  cooldown_seconds: number;
  window_seconds: number;
  failure_count_threshold: number;
  conditions: Array<{ fact: string; comparison: "gte" | "gt" | "lte" | "lt" | "eq"; value: boolean | number | string }>;
  ai_assisted: boolean;
  safe_behavior: "suppress" | "signal" | "fail_closed";
  tenant_fact_allowlist: string[];
}

export interface MonitoringActionPolicy {
  policy_ref: string;
  policy_revision: number;
  target_ref: string;
  min_severity: MonitoringSeverity;
  min_confidence: number;
  critical_fail_closed: boolean;
  critical_directives: Array<{
    operator: MonitoringOperator | "*";
    action_kind: MonitoringAction;
    target_ref: string;
    min_severity: MonitoringSeverity;
    min_confidence: number;
  }>;
}

export interface MonitoringRule {
  id: string;
  revision: number;
  channel: UnifiedChannelId;
  operator: MonitoringOperator;
  enabled: boolean;
  action: MonitoringAction;
  recipients: string[];
  rule_authority: MonitoringRuleAuthority;
  action_policy: MonitoringActionPolicy;
  effective_at: string | null;
  created_at: string | null;
}

export interface MonitoringRulesResponse {
  ok: boolean;
  result?: {
    rules: MonitoringRule[];
    can_manage: boolean;
    recipient_options: Array<{
      ref: string; kind: "team" | "operator" | "recipient-group"; label: string;
    }>;
  };
  error?: string | null;
}

export interface MonitoringAuditItem {
  ts: string | null;
  event: string;
  actor_ref: string;
  rule_ref: string;
  revision: number | null;
  channel: UnifiedChannelId | null;
  operator: MonitoringOperator | null;
  reason: string | null;
}

export const monitoringApi = {
  rules: (channel?: UnifiedChannelId, signal?: AbortSignal) => {
    const suffix = channel ? `?channel=${encodeURIComponent(channel)}` : "";
    return api.get<MonitoringRulesResponse>(`/api/monitoring/rules${suffix}`, signal);
  },
  saveRule: (
    id: string,
    body: {
      expected_revision: number | null;
      channel: UnifiedChannelId;
      operator: MonitoringOperator;
      enabled: boolean;
      action_kind: MonitoringAction;
      recipient_refs: string[];
      rule_authority: MonitoringRuleAuthority;
      action_policy: MonitoringActionPolicy;
    },
    signal?: AbortSignal,
  ) => api.put<{ ok: boolean; result?: { rule: MonitoringRule }; error?: string }>(
    `/api/monitoring/rules/${encodeURIComponent(id)}`, body, signal,
  ),
  alerts: (cursor = 0, limit = 100, signal?: AbortSignal) =>
    api.get<MonitoringAlertsResponse>(
      `/api/monitoring/alerts?cursor=${cursor}&limit=${limit}`, signal,
    ),
  receipt: (signalId: string, cursor: number, revision: number, signal?: AbortSignal) =>
    api.post<{ ok: boolean; result?: { recorded: boolean; latency_ms: number }; error?: string }>(
      `/api/monitoring/alerts/${encodeURIComponent(signalId)}/receipt`,
      { cursor, revision }, signal,
    ),
  acknowledge: (signalId: string, expectedRevision: number, reason = "triaged", signal?: AbortSignal) =>
    api.post<MonitoringAlertMutationResponse>(
      `/api/monitoring/alerts/${encodeURIComponent(signalId)}/acknowledge`,
      { expected_revision: expectedRevision, reason }, signal,
    ),
  resolve: (signalId: string, expectedRevision: number, reason = "handled", signal?: AbortSignal) =>
    api.post<MonitoringAlertMutationResponse>(
      `/api/monitoring/alerts/${encodeURIComponent(signalId)}/resolve`,
      { expected_revision: expectedRevision, reason }, signal,
    ),
  takeover: (signalId: string, expectedRevision: number, requestedOperatorId?: string, signal?: AbortSignal) => {
    const body: { expected_revision: number; reason: string; requested_operator_id?: string } = {
      expected_revision: expectedRevision, reason: "escalated",
    };
    if (requestedOperatorId) body.requested_operator_id = requestedOperatorId;
    return api.post<MonitoringAlertMutationResponse>(
      `/api/monitoring/alerts/${encodeURIComponent(signalId)}/takeover`,
      body, signal,
    );
  },
  pause: (signalId: string, expectedRevision: number, signal?: AbortSignal) =>
    api.post<MonitoringAlertMutationResponse>(
      `/api/monitoring/alerts/${encodeURIComponent(signalId)}/pause`,
      { expected_revision: expectedRevision, reason: "policy_review" }, signal,
    ),
  resume: (signalId: string, expectedRevision: number, signal?: AbortSignal) =>
    api.post<MonitoringAlertMutationResponse>(
      `/api/monitoring/alerts/${encodeURIComponent(signalId)}/resume`,
      { expected_revision: expectedRevision, reason: "handled" }, signal,
    ),
  approve: (signalId: string, expectedRevision: number, signal?: AbortSignal) =>
    api.post<MonitoringAlertMutationResponse>(
      `/api/monitoring/alerts/${encodeURIComponent(signalId)}/approve`,
      { expected_revision: expectedRevision, reason: "policy_review" }, signal,
    ),
  reject: (signalId: string, expectedRevision: number, signal?: AbortSignal) =>
    api.post<MonitoringAlertMutationResponse>(
      `/api/monitoring/alerts/${encodeURIComponent(signalId)}/reject`,
      { expected_revision: expectedRevision, reason: "policy_review" }, signal,
    ),
  audit: (logicalRuleId: string, limit = 4, signal?: AbortSignal) =>
    api.get<{ ok: boolean; result?: { total: number; items: MonitoringAuditItem[] }; error?: string }>(
      `/api/monitoring/audit?logical_rule_id=${encodeURIComponent(logicalRuleId)}&limit=${limit}`,
      signal,
    ),
};

// ---------------------------------------------------------------------------
// Browser voice operator API. Assignment and tenant authority always come
// from the cookie-scoped server reads; call ids below are opaque Satatam ids.
// ---------------------------------------------------------------------------
export type VoiceAvailability = "offline" | "available" | "busy" | "wrap_up";
export type VoiceCallState = "offered" | "active" | "held" | "declined" | "completed";

export interface VoicePresenceResult {
  channel_enabled: boolean;
  setup_state: string;
  availability: VoiceAvailability;
  revision: number;
  stale_after: string | null;
}
export interface VoicePresenceResponse {
  ok: boolean;
  result: VoicePresenceResult;
  error?: string;
}
export interface VoiceSetPresenceResponse {
  ok: boolean;
  availability: VoiceAvailability;
  revision: number;
  stale_after: string | null;
  channel_enabled: boolean;
  setup_state: string;
  error?: string;
}
export interface VoiceTokenResponse {
  ok: boolean;
  token: string;
  expires_in: number;
  error?: string;
}
export interface VoicePublicCall {
  call_id: string;
  assignment_id: string;
  state: "offered" | "active" | "held";
  revision: number;
  session_id: string;
  channel: "voice_inbound" | "voice_outbound";
  transfer_phase: string | null;
  transfer_outcome: string | null;
  handoff: VoiceHandoffPacket;
}
export interface VoiceActiveCallResponse {
  ok: boolean;
  call: VoicePublicCall | null;
  error?: string;
}
/**
 * The answer to an operator-initiated outbound call.
 *
 * `blockers` is only ever present with `error === "outbound_voice_blocked"`
 * (HTTP 422): it is the list of policy gates the call did not clear, in the
 * server's own vocabulary, and it carries no provider detail. Every other
 * refusal is a bare `{ok: false, error}`.
 */
export interface VoiceOutboundResponse {
  ok: boolean;
  call_id?: string;
  status?: string;
  error?: string | null;
  blockers?: string[];
}
/**
 * The purpose an operator-placed call carries on the wire.
 *
 * `support_followup` is the product's CONTACT-consent purpose for support work
 * (docs/SECURITY.md, "Consent purposes and precedence"); `support` is the
 * AI-memory purpose and is not a contact authorization. The outbound gate
 * compares this string against the tenant's `allowed_purposes` and reads
 * consent on channel `voice_outbound` with it, so it is typed as a literal
 * rather than a bare `string`: a caller that invents a purpose the gate's
 * vocabulary does not contain would be refused with `purpose_not_allowed`,
 * and that is a compile error here rather than a 422 in front of an operator.
 */
export type VoiceOutboundPurpose = "support_followup";

/** The consent channel the outbound gate itself reads. */
export const VOICE_OUTBOUND_CONSENT_CHANNEL = "voice_outbound";

export interface VoiceActionResponse {
  ok: boolean;
  call_id: string;
  state: VoiceCallState;
  revision: number;
  transfer_phase?: string | null;
  transfer_outcome?: string | null;
  assigned_operator_id?: string | null;
  error?: string;
}

const voiceAction = (
  action: "accept" | "decline" | "hold" | "resume" | "complete",
  callId: string,
  assignmentId: string,
  expectedRevision: number,
  signal?: AbortSignal,
) => api.post<VoiceActionResponse>(
  `/api/voice/calls/${encodeURIComponent(callId)}/${action}`,
  { assignment_id: assignmentId, revision: expectedRevision },
  signal,
);

export const voiceApi = {
  presence: (signal?: AbortSignal) =>
    api.get<VoicePresenceResponse>("/api/voice/presence", signal),
  setPresence: (
    availability: VoiceAvailability,
    expectedRevision: number,
    signal?: AbortSignal,
  ) => api.post<VoiceSetPresenceResponse>(
    "/api/voice/presence",
    { availability, revision: expectedRevision },
    signal,
  ),
  token: (direction: "inbound" | "outbound", signal?: AbortSignal) =>
    api.post<VoiceTokenResponse>("/api/voice/token", { direction }, signal),
  /**
   * Place one outbound call to a canonical customer.
   *
   * `idempotencyKey` is minted once per composer and REUSED on every retry of
   * the same intent -- that header is the only thing standing between a
   * flaky network and a customer whose phone rings twice.
   */
  outbound: (
    customerId: string,
    purpose: VoiceOutboundPurpose,
    idempotencyKey: string,
    signal?: AbortSignal,
  ) => api.post<VoiceOutboundResponse>(
    "/api/voice/outbound",
    { customer_id: customerId, purpose },
    signal,
    { "Idempotency-Key": idempotencyKey },
  ),
  activeCall: (signal?: AbortSignal) =>
    api.get<VoiceActiveCallResponse>("/api/voice/calls/active", signal),
  accept: (
    callId: string, assignmentId: string, expectedRevision: number,
    signal?: AbortSignal,
  ) => voiceAction("accept", callId, assignmentId, expectedRevision, signal),
  decline: (
    callId: string, assignmentId: string, expectedRevision: number,
    signal?: AbortSignal,
  ) => voiceAction("decline", callId, assignmentId, expectedRevision, signal),
  hold: (
    callId: string, assignmentId: string, expectedRevision: number,
    signal?: AbortSignal,
  ) => voiceAction("hold", callId, assignmentId, expectedRevision, signal),
  resume: (
    callId: string, assignmentId: string, expectedRevision: number,
    signal?: AbortSignal,
  ) => voiceAction("resume", callId, assignmentId, expectedRevision, signal),
  complete: (
    callId: string, assignmentId: string, expectedRevision: number,
    signal?: AbortSignal,
  ) => voiceAction("complete", callId, assignmentId, expectedRevision, signal),
};

export interface CustomerIdentitySummary {
  identity_id: string;
  channel: InboxChannel;
  label: string;
  verification_status: string;
  verification_strength: string | null;
  verified_at: string | null;
}

export interface CustomerMemorySummary {
  memory_id: string;
  purpose: string;
  channel: string;
  sensitivity: string;
  content: string;
  source: string;
  retention_expires_at: string;
  created_at: string;
}

export interface CustomerConsentSummary {
  allowed: boolean;
  reason: string;
}

export interface IdentityReviewSummary {
  review_id: string;
  identity_id: string;
  proposed_customer_id: string;
  status: string;
  reason_code: string;
  created_at: string | null;
  resolved_at: string | null;
  identity: {
    channel: string;
    verification_status: string | null;
    verification_strength: string | null;
    verified_at: string | null;
    created_at: string | null;
  };
  candidate: {
    status: string | null;
    created_at: string | null;
  };
}

export interface CustomerProfile {
  customer: {
    customer_id: string;
    status: string;
    created_at: string | null;
    updated_at: string | null;
  };
  identities: CustomerIdentitySummary[];
  memory: CustomerMemorySummary[];
  consent: Record<string, CustomerConsentSummary>;
  pending_reviews: IdentityReviewSummary[];
  pending_reviews_next_offset: number | null;
  active_merge: {
    merge_id: string;
    source_customer_id: string;
    target_customer_id: string;
    merged_at: string | null;
  } | null;
}

export interface CustomerProfileResponse {
  ok: boolean;
  result?: CustomerProfile;
  error?: { code: string; message: string } | null;
}

export interface CustomerMutationResponse {
  ok: boolean;
  result?: {
    review?: IdentityReviewSummary;
    merge?: { status: "complete" | "conflict"; merge_id: string; audit_id: number };
  };
  error?: { code: string; message: string } | null;
}

export const customersApi = {
  profile: (
    customerId: string,
    purpose: string,
    channel: string,
    options: {
      reviewLimit?: number;
      reviewOffset?: number;
      signal?: AbortSignal;
    } = {},
  ) => {
    const qs = new URLSearchParams({ purpose, channel });
    if (options.reviewLimit !== undefined) qs.set("review_limit", String(options.reviewLimit));
    if (options.reviewOffset !== undefined) qs.set("review_offset", String(options.reviewOffset));
    return api.get<CustomerProfileResponse>(
      `/api/customers/${encodeURIComponent(customerId)}?${qs.toString()}`,
      options.signal,
    );
  },
  resolveReview: (reviewId: string, resolution: "approved" | "rejected") =>
    api.post<CustomerMutationResponse>(
      `/api/customers/identity-reviews/${encodeURIComponent(reviewId)}/resolve`,
      { resolution, expected_status: "pending" },
    ),
  merge: (sourceCustomerId: string, targetCustomerId: string) =>
    api.post<CustomerMutationResponse>("/api/customers/merge", {
      source_customer_id: sourceCustomerId,
      target_customer_id: targetCustomerId,
    }),
  undoMerge: (mergeId: string) =>
    api.post<CustomerMutationResponse>(
      `/api/customers/merges/${encodeURIComponent(mergeId)}/undo`,
      { expected_state: "merged" },
    ),
};

export interface AnalyticsDay {
  date: string;
  /** Widget sessions opened. The widget opens one when the page loads, so this
   *  counts loads -- `conversations` is the number a human would call a chat. */
  sessions: number;
  /** Sessions carrying at least one customer message. */
  conversations: number;
  messages: number;
  resolved: number;
  escalated: number;
  deflection_rate: number | null;
  csat_avg: number | null;
  csat_count: number;
}
export interface AnalyticsDelta {
  absolute: number | null;
  percent: number | null;
}
export interface AnalyticsPeriod {
  timezone: string;
  days: number;
  current_start: string;
  current_end: string;
  prior_start: string;
  prior_end: string;
}
export type AnalyticsTotals = Omit<AnalyticsDay, "date">;
export type AnalyticsMetric = keyof AnalyticsTotals;
export interface AnalyticsSummaryResult {
  days: AnalyticsDay[];
  totals: AnalyticsTotals;
  prior_totals: AnalyticsTotals;
  deltas: Record<AnalyticsMetric, AnalyticsDelta>;
  period: AnalyticsPeriod;
  spend: {
    plan: string | null;
    used: number | null;
    ceiling: number | null;
    pct: number | null;
    // Estimated pre-cutover AI cost retained for historical reporting only.
    historical_ai_cost_usd_month?: number | null;
    // Prompt-cache reuse this month, from live gateway events. Absent on
    // older backends or when the read failed -- render nothing, never 0%.
    ai_cache?: {
      input_tokens: number;
      cached_input_tokens: number;
      cache_write_tokens: number;
      reuse_share: number | null;
    };
    overage_estimate?: OverageEstimate | null;
  };
  // Measured first-reply speed against the tenant's own AI Studio target.
  // `met_pct` is null when no streamed turns were measured in the window --
  // which must render as "no data", never as 0%.
  reply_speed?: {
    target_ms: number | null;
    samples: number;
    mean_ms: number | null;
    met: number;
    met_pct: number | null;
    days: number;
  };
}
export interface AnalyticsSummaryResponse {
  ok: boolean;
  result?: AnalyticsSummaryResult;
  error?: string;
}

export interface AnalyticsOutcomesResponse {
  ok: boolean;
  result?: {
    eligible_conversations: number;
    verified_resolutions: number;
    verified_resolution_rate: number | null;
    meets_guarantee_minimum_volume: boolean;
  };
  error?: string | null;
}

export interface AnalyticsActivationResponse {
  ok: boolean;
  result?: {
    assistant_identity_configured: boolean;
    widget_session_observed: boolean;
    first_widget_traffic_observed: boolean;
  };
  error?: string | null;
}

export interface MonitoringAnalyticsResponse {
  ok: boolean;
  result?: {
    operators: Array<{ operator: MonitoringOperator; count: number }>;
    revisions: Array<{ revision: number; count: number }>;
    severities: Array<{ severity: MonitoringSeverity; count: number }>;
    actions: Array<{ action: MonitoringAction; count: number }>;
    false_positive: { count: number; reviewed: number };
    handoff_outcomes: Array<{ outcome: string; count: number }>;
    receive_to_persist_latency_ms: {
      count: number; p50: number | null; p95: number | null; p99: number | null;
    };
    receive_to_dashboard_latency_ms: {
      available: boolean; count: number;
      p50: number | null; p95: number | null; p99: number | null;
    };
  };
  error?: string | null;
}

export interface DsarRow {
  request_id: string;
  request_type: string;
  status: string;
  requested_at: string | null;
  completed_at: string | null;
}
export interface PrivacyExportResponse {
  ok: boolean;
  result?: {
    subject_hash: string;
    generated_at: string;
    sessions: unknown[];
    note: string;
  };
  error?: string;
}
export interface PrivacyErasureResponse {
  ok: boolean;
  result?: { request_id: string; sessions_erased?: number; status?: string; note?: string };
  error?: string;
}
export interface DsarListResponse {
  ok: boolean;
  result?: { requests: DsarRow[] };
  error?: string;
}

export interface AccountExportPage {
  format_version: number;
  tenant_id: string;
  conversations: unknown[];
  conversation_count: number;
  next_cursor: string | null;
  complete: boolean;
  account?: Record<string, unknown>;
  knowledge_base?: unknown[];
}
export interface AccountExportResponse {
  ok: boolean;
  result?: AccountExportPage;
  error?: string;
}

export const privacyApi = {
  export: (identifier: string) =>
    api.post<PrivacyExportResponse>("/api/privacy/export", { identifier }),
  erase: (identifier: string) =>
    api.post<PrivacyErasureResponse>("/api/privacy/erasure", { identifier }),
  list: () => api.get<DsarListResponse>("/api/privacy/dsar"),
  // The whole account, not one subject. Paginated: keep passing the cursor
  // back until `next_cursor` is null, or the export silently stops at the
  // first page and claims to be everything.
  accountExport: (cursor?: string | null) =>
    api.get<AccountExportResponse>(
      `/api/privacy/account-export${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`,
    ),
};

export interface UnresolvedItem {
  session_id: string;
  first_question: string | null;
  status: string;
  escalation_count: number;
  /** Why this row surfaced: "negative_csat" | "needs_human" | "escalated" | "low_confidence". */
  reason?: string | null;
  /** The assistant's last confidence (0..1), or null when the session never recorded one. */
  ai_confidence?: number | null;
  updated_at: string | null;
}
export interface UnresolvedResponse {
  ok: boolean;
  result?: { total: number; items: UnresolvedItem[] };
  error?: string;
}

export const analyticsApi = {
  summary: (days = 30, signal?: AbortSignal) =>
    api.get<AnalyticsSummaryResponse>(`/api/analytics/summary?days=${days}`, signal),
  outcomes: (days = 30, signal?: AbortSignal) =>
    api.get<AnalyticsOutcomesResponse>(`/api/analytics/outcomes?days=${days}`, signal),
  unresolved: (days: number) =>
    api.get<UnresolvedResponse>(`/api/analytics/unresolved?days=${days}`),
  monitoring: (days: number, signal?: AbortSignal) =>
    api.get<MonitoringAnalyticsResponse>(`/api/analytics/monitoring?days=${days}`, signal),
  activation: (signal?: AbortSignal) =>
    api.get<AnalyticsActivationResponse>("/api/analytics/activation", signal),
};

export const billingApi = {
  // Returns a Stripe Checkout URL; caller redirects (window.location.assign).
  checkout: (plan: BillingPlan, billingPeriod: BillingPeriod) =>
    api.post<BillingUrlResponse>("/api/billing/checkout", {
      plan,
      billing_period: billingPeriod,
    }),
  // Returns a Stripe Billing Portal URL; caller redirects.
  portal: () => api.post<BillingUrlResponse>("/api/billing/portal"),
  // The tenant's real plan + billing state (Settings page).
  status: (signal?: AbortSignal) =>
    api.get<BillingStatusResponse>("/api/billing/status", signal),
  // The sold ladder with per-plan checkout availability (upgrade modal). Public
  // — it is the pricing page's own content and carries nothing tenant-specific.
  plans: (signal?: AbortSignal) =>
    api.get<BillingPlansResponse>("/api/billing/plans", signal),
  // What this tenant's plan carries, so a gated control can explain itself
  // before it is clicked rather than answering with a 402 afterwards.
  entitlements: (signal?: AbortSignal) =>
    api.get<EntitlementsResponse>("/api/billing/entitlements", signal),
  // The receptionist V3.2 ladder + per-plan buyability (W1 "Add a service"
  // card). Public and read-only, same treatment as `plans` above.
  receptionistPlans: (signal?: AbortSignal) =>
    api.get<ReceptionistPlansResponse>("/api/billing/receptionist/plans", signal),
  // Starts a receptionist Checkout session. Owner-only server-side; gated off
  // in staging unless APP_ENV=staging, RECEPTIONIST_STAGING_CHECKOUT_ENABLED=1,
  // and a test Stripe key are all present, and gated off in production unless
  // RECEPTIONIST_CHECKOUT_ENABLED=1, a live Stripe key, and the full V3.2
  // live Price catalog are all present (see src/billing/routes.py and
  // src/billing/receptionist_checkout_live.py). Returns a Stripe URL to
  // redirect to, exactly like the website `checkout` above.
  receptionistCheckout: (planName: string) =>
    api.post<BillingUrlResponse>("/api/billing/receptionist/checkout", { plan_name: planName }),
  // The receptionist's own usage meter (D3) -- a separate meter from the web
  // conversation usage above; never combined into one number.
  receptionistUsage: (signal?: AbortSignal) =>
    api.get<ReceptionistUsageResponse>("/api/billing/usage/receptionist", signal),
  // W5: the operator-app Calls view -- tenant-scoped, paginated, owner AND
  // member (Inbox's own access rule). A SEPARATE path from Codex-owned
  // GET /api/receptionist/calls (owner-only, unpaginated) -- see
  // src/billing/receptionist_calls.py's module docstring for why.
  receptionistCalls: (cursor?: string, limit = 25, signal?: AbortSignal) => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (cursor) params.set("cursor", cursor);
    return api.get<ReceptionistCallsResponse>(`/api/billing/receptionist/calls?${params.toString()}`, signal);
  },
};

export interface ReceptionistPlanListing {
  plan_name: string;
  monthly_usd: string;
  included_minutes: number;
  overage_per_minute_usd: string;
  purchasable: boolean;
  reason: string | null;
}

export interface ReceptionistPlansResponse {
  ok: boolean;
  catalog_version?: string;
  checkout_enabled?: boolean;
  plans?: ReceptionistPlanListing[];
  error?: string;
}

// W10: present only while overage billing is on. An ESTIMATE over the Stripe
// billing period that is actually billed (not the calendar month); null when
// the subscription is not metered. Render via shared/overageEstimate.mjs.
export interface OverageEstimate {
  estimate: true;
  unit: "conversation" | "minute";
  period_start: string;
  period_end: string;
  used: number;
  included: number;
  overage: number;
  estimated_amount_usd: string;
}

export interface ReceptionistUsageResponse {
  ok: boolean;
  period_start?: string;
  period_end?: string;
  used_seconds?: number;
  used_minutes?: number;
  included_minutes?: number | null;
  overage_minutes?: number;
  plan_name?: string | null;
  overage_estimate?: OverageEstimate | null;
  error?: string;
}

// W5: one call row from GET /api/billing/receptionist/calls. Every
// caller-identifying field is null and `caller_details` reads
// "withheld_pending_notice" until a per-call consent record exists --
// see src/billing/receptionist_calls.py.
export interface ReceptionistCallRow {
  call_id: string;
  started_at: string | null;
  answered_at: string | null;
  ended_at: string | null;
  status: string | null;
  outcome: string | null;
  billable_seconds: number | null;
  caller_details: "withheld_pending_notice" | "available";
  caller_number: string | null;
  caller_name: string | null;
  transcript: string | null;
  summary: string | null;
}

export interface ReceptionistCallsResponse {
  ok: boolean;
  result: { calls: ReceptionistCallRow[]; next_cursor: string | null } | null;
  error: string | null;
}

// ---------------------------------------------------------------------------
// Satatam Help -- the in-app guidance assistant (POST /api/help/ask).
// ---------------------------------------------------------------------------

export interface HelpLink {
  label: string;
  href: string;
}

export interface HelpAskResult {
  answer: string;
  links: HelpLink[];
  needs_human: boolean;
}

export interface HelpAskResponse {
  ok: boolean;
  error?: string | null;
  result?: HelpAskResult | null;
  /**
   * Only on the 429 (`help_daily_cap`), where `result` is null: the cap the
   * blocked-copy sentence quotes. Read off the thrown `ApiError`'s body with
   * `apiErrorNumber(err, "daily_cap")` -- the server's number, so the app does
   * not have to be redeployed to stop lying when the cap changes.
   */
  daily_cap?: number;
}

export interface HelpStreamHandlers {
  /** One fragment of the answer, in order. Never contains the server's
   *  `<<meta>>` trailer -- the route withholds it. */
  onDelta: (text: string) => void;
  /** Once, after the last delta. */
  onMeta: (meta: { links: HelpLink[]; needs_human: boolean }) => void;
}

export const helpApi = {
  // The server allowlists every link it returns, so the client renders only
  // what it is handed and never interprets an answer as markup.
  ask: (
    question: string,
    history: { role: "user" | "assistant"; text: string }[],
    page: string | null,
    signal?: AbortSignal,
  ) =>
    api.post<HelpAskResponse>(
      "/api/help/ask",
      { question, history, ...(page ? { page } : {}) },
      signal,
    ),

  /**
   * The same question, answered as it is written.
   *
   * Not `api.post`: that reads the whole body before returning, which is the
   * one thing streaming exists to avoid. Refusals still arrive as JSON with a
   * status -- the cap, the validation errors, the outage -- so they are
   * rethrown as the same `ApiError` the blocking path throws and the drawer's
   * existing copy handles them unchanged. A 200 that is NOT an event stream
   * is a server older than this client; it is read as JSON and replayed as
   * one delta plus one meta, so a mid-deploy tab still gets an answer.
   */
  askStreaming: async (
    question: string,
    history: { role: "user" | "assistant"; text: string }[],
    page: string | null,
    handlers: HelpStreamHandlers,
    signal?: AbortSignal,
  ): Promise<void> => {
    const res = await fetch(`${BASE}/api/help/ask`, {
      method: "POST",
      headers: { Accept: "text/event-stream", "Content-Type": "application/json" },
      body: JSON.stringify({ question, history, ...(page ? { page } : {}), stream: true }),
      credentials: "include",
      signal,
    });
    if (!res.ok || !res.headers.get("content-type")?.includes("text/event-stream")) {
      const text = await res.text();
      let parsed: unknown = null;
      try {
        parsed = text ? JSON.parse(text) : null;
      } catch {
        /* Non-JSON error page. `parsed` stays null, as in `request`. */
      }
      if (!res.ok) {
        const record = parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : null;
        const code = record && "error" in record ? String(record.error) : null;
        throw new ApiError(res.status, code ?? `${res.status} ${res.statusText}`, parsed);
      }
      const body = parsed as HelpAskResponse | null;
      if (!body?.ok || !body.result) throw new ApiError(200, "help_unavailable", parsed);
      handlers.onDelta(body.result.answer);
      handlers.onMeta({ links: body.result.links, needs_human: body.result.needs_human });
      return;
    }
    const reader = res.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      // Frames are separated by a blank line; a partial one stays in the
      // buffer until the rest of it arrives.
      let split = buffer.indexOf("\n\n");
      while (split !== -1) {
        const frame = buffer.slice(0, split);
        buffer = buffer.slice(split + 2);
        let event = "";
        const data: string[] = [];
        for (const line of frame.split("\n")) {
          if (line.startsWith("event: ")) event = line.slice(7);
          else if (line.startsWith("data: ")) data.push(line.slice(6));
        }
        const payload = data.join("\n");
        if (event === "delta") handlers.onDelta(payload);
        else if (event === "meta") {
          try {
            const meta = JSON.parse(payload) as { links: HelpLink[]; needs_human: boolean };
            handlers.onMeta(meta);
          } catch {
            handlers.onMeta({ links: [], needs_human: false });
          }
        } else if (event === "error") {
          /* The streamed spelling of the JSON path's error envelope. It is
             rebuilt into that envelope here so `apiErrorCode` reads it, and
             the drawer's copy and Retry control treat a stream that failed
             mid-flight exactly as they treat a refusal that never streamed.
             A frame that will not parse is still an outage. */
          let code = "help_unavailable";
          let message: string | null = null;
          try {
            const frameBody = JSON.parse(payload) as { code?: unknown; message?: unknown };
            if (typeof frameBody.code === "string" && frameBody.code) code = frameBody.code;
            if (typeof frameBody.message === "string" && frameBody.message) message = frameBody.message;
          } catch {
            /* Unparseable frame: the defaults above stand. */
          }
          throw new ApiError(503, message ?? code, { ok: false, error: code, result: null });
        }
        split = buffer.indexOf("\n\n");
      }
    }
  },
};

export type HexColor = `#${string}`;

/** The widget header's action buttons. Mirrors HEADER_ACTIONS in
 *  tools/appearance_studio_config.py and the table in WidgetStage.tsx. */
export type WidgetHeaderAction =
  | "minimize" | "close" | "restart" | "sound" | "menu" | "email transcript";

export interface WidgetAppearance {
  version: 1;
  launcher: {
    icon: "eye" | "chat" | "spark" | "question" | "headset" | "initials" | "logo";
    shape: "circle" | "rounded" | "pill" | "square";
    size: "small" | "medium" | "large";
    label: string;
    background: HexColor;
    foreground: HexColor;
    logo_data_url: string | null;
  };
  avatar: {
    mode: "eye" | "initials" | "image";
    image_data_url: string | null;
    /** Up to 3 characters, drawn by both the header avatar and the `initials`
     *  launcher icon. Empty falls back to the company name's first letters, so
     *  a tenant who never opens this control still gets a sensible mark. */
    initials: string;
  };
  colors: {
    accent: HexColor;
    on_accent: HexColor;
    header: HexColor;
    header_text: HexColor;
    user_bubble: HexColor;
    user_text: HexColor;
    /** "auto" (default) leaves the widget's own light/dark theme alone --
     *  surface/text/etc. below are inert. "custom" makes them the widget's
     *  panel background, body text, hairline, and agent bubble in both
     *  schemes. Mirrors tools/appearance_config.py's SURFACE_MODES. */
    surface_mode: "auto" | "custom";
    surface: HexColor;
    surface_2: HexColor;
    text: HexColor;
    muted: HexColor;
    border: HexColor;
    agent_bubble: HexColor;
    agent_text: HexColor;
  };
  panel: {
    size: "compact" | "standard" | "spacious" | "tall";
    shape: "soft" | "rounded" | "square";
  };
  suggestions: string[];
  placement: {
    position: "br" | "bl";
    offset_x: number;
    offset_y: number;
    z_index: number;
  };
}

export interface OperatorBranding {
  version: 1;
  display_name: string;
  accent: HexColor;
  on_accent: HexColor;
  logo_data_url: string | null;
  /** Wash the console's page backdrop with the accent. Opt-in, and honoured
   *  only while the accent also clears the tinted-ground legibility gate. */
  console_surface_tint: boolean;
  /** Keep the shipped palette, or use separate tenant-selected dark and light surfaces. */
  console_theme_mode: "default" | "custom";
  console_background: HexColor;
  console_foreground: HexColor;
  console_light_background: HexColor;
  console_light_foreground: HexColor;
}

export type AppearanceFieldErrors = Record<string, string>;

// Mirrors tools/appearance_studio_config.py's DEFAULT_WIDGET_APPEARANCE_EXTRAS /
// normalize_widget_appearance_extras exactly. Namespaced separately from
// `WidgetAppearance` above (which the live widget runtime actually renders):
// these fields are captured, validated, and persisted, but not yet consumed
// by static/landing/widget/customer-service-pro.js -- same honest-disclosure
// pattern as AriaPersona.compose_system_prompt not yet being read by the
// reply engine. Appearance Studio's own preview simulates them via CSS
// instead of the real runtime until that wiring lands.
export interface WidgetAppearanceExtras {
  version: 1;
  theme: { preset: string; scheme: "auto" | "light" | "dark"; density: "compact" | "standard" | "roomy" };
  gradient: { header_enabled: boolean; angle: number; panel_fill: "solid" | "gradient" | "pattern" };
  launcher_extra: {
    label_mode: "on" | "hover" | "off";
    shadow: "none" | "soft" | "medium" | "high" | "dramatic";
    animation: "none" | "pulse" | "bounce" | "glow";
    badge_enabled: boolean;
    badge_count: string;
    teaser_enabled: boolean;
    teaser_text: string;
    teaser_delay_s: number;
    mobile_hide: boolean;
  };
  panel_extra: {
    width: number;
    height: number;
    radius: number;
    border_width: number;
    elevation: "none" | "soft" | "medium" | "high" | "dramatic";
    blur: number;
    glass: boolean;
    mobile_behavior: "full" | "inset" | "sheet";
  };
  header: {
    layout: "compact" | "centered" | "hero" | "minimal";
    avatar_size: number;
    avatar_radius_pct: number;
    status_dot: boolean;
    subtitle_visible: boolean;
    /** The line `subtitle_visible` shows. Without it the toggle governed an
     *  empty string. */
    subtitle_text: string;
    /* Constrained rather than `string[]`: tools/appearance_studio_config.py already
       rejects anything outside HEADER_ACTIONS, and the widget only renders keys in
       its own lookup table, so an unconstrained type let the studio offer -- and
       persist -- an action that would silently never appear. */
    actions: WidgetHeaderAction[];
  };
  messages: {
    bubble_style: "solid" | "outline" | "flat" | "elevated";
    radius: number;
    tail: boolean;
    avatar_per_message: boolean;
    stamps: boolean;
    agent_label: boolean;
    cites: boolean;
    typing_style: "dots" | "bar" | "text";
    max_width_pct: number;
    gap: number;
  };
  composer: {
    /** Empty on purpose: the widget ships a per-language placeholder
     *  ("Message…", "Mensaje…", "Nachricht…"), and a tenant value overrides it
     *  in *every* language. Empty means "keep the localised default". */
    placeholder: string;
    send_style: "label" | "icon" | "ghost";
    attach: boolean;
    emoji: boolean;
    voice: boolean;
    quick_reply_style: "pill" | "ghost" | "card" | "off";
    footer: boolean;
    footer_text: string;
    background: string;
    badge_color: HexColor;
  };
  typography: {
    font: "system" | "geist" | "display" | "serif" | "rounded" | "mono";
    size: number;
    title_size: number;
    title_weight: number;
    line_height: number;
    letter_spacing: number;
  };
  motion: {
    open_style: "scale" | "slide" | "fade" | "sheet";
    duration_ms: number;
    easing: "spring" | "out" | "drawer" | "linear";
    message_entrance: "rise" | "fade" | "pop" | "none";
    launcher_hover: "lift" | "grow" | "tilt" | "none";
    reduce_motion: boolean;
  };
  content: {
    away_message: string;
    csat_prompt: string;
    escalate_line: string;
    language: "en" | "es" | "fr" | "de" | "pt" | "ja" | "ar" | "auto";
    rtl: boolean;
  };
  accessibility: {
    focus_style: "ring" | "outline" | "underline";
    focus_color: HexColor;
    focus_width: number;
    min_font_size: number;
    shortcut: "alt+/" | "ctrl+k" | "esc" | "none";
    launcher_aria_label: string;
    announce_new_messages: boolean;
    high_contrast_toggle: boolean;
  };
  advanced: { custom_css: string };
}

// Mirrors tools/appearance_studio_config.py's DEFAULT_WHITE_LABEL / normalize_
// white_label. How far the tenant's identity propagates into the operator
// dashboard chrome itself -- broadcast only on Publish, per writeTenant()'s
// established contract (see aria-context.tsx's identical comment).
export interface WhiteLabel {
  version: 1;
  apply: "mark" | "accent" | "full";
  replace_product_name: boolean;
  workspace_label: string;
  use_logo_as_mark: boolean;
  use_logo_as_favicon: boolean;
  rename_browser_tab: boolean;
  show_powered_by: boolean;
}

// Mirrors tools/aria_persona_config.py's DEFAULT_ARIA_PERSONA / normalize_aria_persona
// exactly -- same field names, same enums, same bounds. Namespaced separately
// from AssistantConfig.behavior (Assistant page free-text fields): this is the assistant
// Studio's structured persona/tone/guardrail/rollout schema.
export type AriaToolId =
  | "order_lookup" | "returns" | "refund" | "sub_change" | "address" | "booking"
  | "invoice" | "password" | "ticket" | "callback" | "escalate" | "kb_write";

export interface AriaPersona {
  version: 1;
  persona: {
    name: string;
    role: string;
    pronouns: "she/her" | "he/him" | "they/them" | "none";
    signature: string;
    mark: "initials" | "glyph" | "photo";
    greet_style: "warm" | "brief" | "formal" | "none";
    emoji: "never" | "sparing" | "free";
    humour: "none" | "light" | "playful";
    self_disclose: "always" | "on-ask" | "footer";
  };
  tone: {
    preset: "concise" | "concierge" | "formal" | "playful" | "technical" | "clinical" | "custom";
    warmth: number; formality: number; verbosity: number;
    directness: number; energy: number; tech: number;
    reading: "grade6" | "grade8" | "grade10" | "pro";
    sentence: "short" | "mixed" | "long";
    spelling: "us" | "uk" | "auto";
  };
  language: {
    primary: string;
    supported: string[];
    detect: boolean;
    fallback: string;
    translate_transcripts: boolean;
    dates: "locale" | "iso" | "us";
    units: "imperial" | "metric" | "locale";
    profanity: "mirror-neutral" | "acknowledge" | "escalate";
  };
  behavior: {
    /* Named for the behaviour, not a magnitude -- this value is quoted into the
       composed system prompt, where "medium" says nothing. tools/aria_persona_config.py
       migrates the legacy low/medium/high values on read. */
    proactivity: "reactive" | "balanced" | "anticipatory";
    act_policy: "ask-then-act" | "act-then-tell" | "ask-always";
    clarify_max: number;
    confidence_floor: number;
    max_turns: number;
    typing_delay_ms: number;
    followup: "none" | "once" | "twice";
    closeout: "summary" | "csat" | "both" | "none";
    summarize_for_operators: boolean;
  };
  knowledge: {
    sources: string[];
    depth: number;
    freshness_days: number;
    semantic_mix: number;
    cite: "always" | "on-policy" | "never";
    unknown: "admit-and-route" | "admit-and-ticket" | "best-effort";
    strictness: number;
  };
  tools: Record<AriaToolId, { on: boolean; approval: "none" | "over-cap" | "always" }>;
  guardrails: {
    banned_topics: string[];
    jailbreak_resistance: "standard" | "high" | "paranoid";
    pii: "redact" | "tokenise" | "store";
    disclaimers: boolean;
    record_notice: boolean;
    markdown: "links-lists" | "full" | "plain";
    refund_ceiling_usd: number;
    discount_ceiling_pct: number;
  };
  escalation: {
    triggers: string[];
    route: "skill-based" | "round-robin" | "owner" | "queue";
    sla_minutes: number;
    after_hours: "ticket" | "callback" | "queue";
    warm_transfer: boolean;
    handoff_line: string;
  };
  memory: {
    cross_session: boolean;
    window_days: number;
    identity_confidence: number;
    remember: string[];
    forget_on_request: boolean;
  };
  voice: {
    tts_voice: "ember" | "slate" | "reed" | "linen" | "granite";
    rate_pct: number;
    pitch_semitones: number;
    barge_in: boolean;
    silence_timeout_s: number;
    dtmf: boolean;
    transcribe: boolean;
    hold_audio: "ambient" | "music" | "silence";
    greeting: string;
  };
  model: {
    primary: "sonnet" | "opus" | "haiku";
    fallback: "opus" | "sonnet" | "none";
    temperature_pct: number;
    max_tokens: number;
    stream: boolean;
    cache: boolean;
    latency_target_ms: number;
    monthly_ceiling_usd: number;
  };
  rollout: {
    canary_pct: number;
    eval_gate_pct: number;
    ab_test: boolean;
    variant: "concise" | "concierge" | "formal" | "playful" | "technical" | "clinical";
    version: string;
    notes: string;
    /** System-set only (by the eval run, not this PUT) — sending it in a PUT
     *  body has no effect; the server always echoes back the last stored value. */
    last_eval_pass_pct: number | null;
  };
}

/** A tenant's refund ceiling. `enabled: false` keeps the numbers so switching
 *  back on does not mean retyping them; they are inert while it is off. */
export interface RefundAutoApproval {
  enabled: boolean;
  max_amount_minor_units?: number;
  currency?: string;
}
export interface ApprovalRules {
  refund_auto_approve?: RefundAutoApproval;
}

export interface AssistantConfig {
  company: string;
  agent_name: string;
  tone: string;
  brand_color: string;
  industry: string;
  greeting: string;
  custom_context: string;
  response_language: string;
  plan: string;
  widget_appearance: WidgetAppearance;
  operator_branding: OperatorBranding;
  aria_persona: AriaPersona;
  widget_appearance_extras: WidgetAppearanceExtras;
  white_label: WhiteLabel;
  business_profile?: Partial<Record<"summary" | "products" | "policies" | "hours" | "dos_donts" | "faq", string>>;
  behavior?: {
    escalation_rules?: string;
    behaviors?: string[];
    topics_to_avoid?: string;
    fallback_message?: string;
    fallback_handoff?: boolean;
    voice_notes?: string;
    business_hours?: {
      enabled?: boolean;
      timezone?: string;
      days?: Record<string, { open: string; close: string }>; // "mon".."sun"
      closed_dates?: string[]; // ISO "YYYY-MM-DD"
    };
  };
  custom_answers?: { question: string; answer: string }[];
  /** Domains this workspace's widget may load on. Empty = platform fallback. */
  allowed_origins?: string[];
  // Wave-5 #41 procedures engine (Tasks 1-2, shipped): top-level, NOT under
  // `behavior`. `strict: true` means the assistant follows ONLY these steps and ignores
  // other knowledge for matching conversations (legal/refund/sensitive flows).
  // An empty list clears the configuration; omitting the key leaves a
  // partial-update request unchanged.
  procedures?: { name: string; trigger: string; strict?: boolean; steps: string[] }[];
  profile_meta?: { generated_at: number; source: string; source_count: number };
  /** Custom approval rules (Growth). Absent or empty means the default: every
   *  refund the assistant proposes waits for a person. */
  approval_rules?: ApprovalRules;
  /** Growth and up. True means the assistant drafts every reply and sends none of them;
   *  each one waits in the inbox for a person. */
  shadow_mode?: boolean;
  /** Automatic re-engagement (the Engagement page's Automation card). False
   *  stops the automated "still there?" text entirely. Absent means the tenant
   *  has never chosen, which is ON — every config predating the card is being
   *  texted today, so the UI must not render an absent value as off. */
  reengage_enabled?: boolean;
  /** How long a chat goes quiet before that text. Server range: 60..86400. */
  reengage_delay_seconds?: number;
}
export interface AssistantResponse {
  ok: boolean;
  result?: {
    config: AssistantConfig;
    options?: { tones: string[]; industries: string[] };
  };
  error?: string;
  field_errors?: AppearanceFieldErrors;
}
/** Why a domain may or may not mint a widget token — from the mint rule itself. */
export type WidgetOriginReason =
  | "tenant_lock"
  | "global_fallback"
  | "not_in_tenant_lock"
  | "not_in_global"
  | "no_allowlist"
  | "dev_open"
  | "no_origin";
export interface WidgetOriginCheck {
  origin: string;
  allowed: boolean;
  reason: WidgetOriginReason;
  message: string;
  checked_against: "workspace" | "platform";
  /** False when the allowlist could not be read — show the answer as unsure. */
  confident: boolean;
  workspace_origins: string[];
}
export interface WidgetOriginCheckResponse {
  ok: boolean;
  result?: WidgetOriginCheck;
  error?: string;
}
export interface GenerateProfileResponse {
  ok: boolean;
  result?: {
    business_profile: Partial<Record<"summary" | "products" | "policies" | "hours" | "dos_donts" | "faq", string>>;
    profile_meta: { generated_at: number; source: string; source_count: number };
  };
  error?: string;
}
export interface AssistantVersion {
  version: number;
  created_at: string;
  active: boolean;
  agent_name: string;
  tone: string;
  /** The operator's own "Version label" from AI Studio's Rollout section. */
  label: string;
  notes: string;
}
export interface AssistantVersionsResponse {
  ok: boolean;
  result?: { versions: AssistantVersion[] };
  error?: string;
}
export interface AssistantRestoreResponse {
  ok: boolean;
  result?: { config: AssistantConfig; restored_from: number; version: number };
  error?: string;
}
export const assistantApi = {
  get: (signal?: AbortSignal) =>
    api.get<AssistantResponse>("/api/assistant", signal),
  versions: (signal?: AbortSignal) =>
    api.get<AssistantVersionsResponse>("/api/assistant/versions", signal),
  /** Republishes an earlier version as a NEW active one; history stays
   *  append-only, so restoring can itself be restored out of. */
  restoreVersion: (version: number, signal?: AbortSignal) =>
    api.post<AssistantRestoreResponse>(
      `/api/assistant/versions/${encodeURIComponent(String(version))}/restore`,
      {},
      signal,
    ),
  update: (fields: Partial<AssistantConfig>, signal?: AbortSignal) =>
    api.put<AssistantResponse>("/api/assistant", fields, signal),
  generateProfile: (sourceUrl?: string) =>
    api.post<GenerateProfileResponse>("/api/assistant/generate-profile", {
      source_url: sourceUrl ?? "",
    }),
  /** Would the widget actually load on this domain? Read-only; mints nothing. */
  checkOrigin: (origin: string, signal?: AbortSignal) =>
    api.get<WidgetOriginCheckResponse>(
      `/api/assistant/widget-origins/check?origin=${encodeURIComponent(origin)}`,
      signal,
    ),
};

// ---------------------------------------------------------------------------
// Proactive on-site engagement rules (E1). Operator CRUD over
// /api/engagement/rules — the widget reads the public list separately. The
// backend is the source of truth for the literal values below; we mirror them
// here so the editor can validate before save and the list can label them.
// ---------------------------------------------------------------------------
export type EngagementMatchType = "contains" | "equals" | "prefix";
export type EngagementTriggerType =
  | "time_on_page"
  | "exit_intent"
  | "immediate"
  | "scroll_depth";
export type EngagementFrequencyCap = "session" | "once_24h" | "always";
// E2: a rule is either an on-site teaser (E1) or a reusable outbound template.
export type EngagementChannel = "on_site" | "email" | "sms";
// E1 richer triggers: target any / new / returning visitor.
export type EngagementAudience = "any" | "new" | "returning";

export interface EngagementRule {
  id: string;
  tenant_id: string;
  name: string;
  enabled: boolean;
  // E2: channel — "on_site" rules carry the E1 fields; "email"/"sms" rules are
  // reusable outbound templates (outbound_subject/outbound_body apply instead).
  channel: EngagementChannel;
  match_path: string;
  match_type: EngagementMatchType;
  trigger_type: EngagementTriggerType;
  trigger_seconds: number;
  // E1 richer triggers: scroll threshold 1..100 (present for "scroll_depth",
  // null otherwise) + the targeted audience (defaults "any").
  trigger_percent: number | null;
  audience: EngagementAudience;
  teaser_text: string;
  // E2 outbound template fields (null for on-site rules).
  outbound_subject: string | null;
  outbound_body: string | null;
  // The step of the automated email sequence this template replaces
  // (tools/queue_followup_email.py STEP_KEYS), or null for a template that is
  // only ever sent by hand from the Inbox.
  step_key?: string | null;
  frequency_cap: EngagementFrequencyCap;
  priority: number;
  created_at: string;
  updated_at: string;
}

/** Body for create/update — server applies its own defaults for omitted optionals. */
export interface EngagementRuleInput {
  name: string;
  enabled?: boolean;
  channel?: EngagementChannel;
  match_path: string;
  match_type: EngagementMatchType;
  trigger_type: EngagementTriggerType;
  trigger_seconds?: number;
  // E1 richer triggers: scroll threshold (null unless "scroll_depth") +
  // audience target (server defaults "any" if omitted).
  trigger_percent?: number | null;
  audience?: EngagementAudience;
  teaser_text: string;
  // E2: outbound template body (email + sms) + subject (email only).
  outbound_subject?: string | null;
  outbound_body?: string | null;
  // "" and null both mean "this template replaces no automated step".
  step_key?: string | null;
  frequency_cap?: EngagementFrequencyCap;
  priority?: number;
}

export interface EngagementListResponse {
  ok: boolean;
  result?: { rules: EngagementRule[] };
  error?: string;
}

/** Per-rule + total impression/engagement counts (E3 analytics). */
export interface EngagementRuleAnalytics {
  rule_id: string;
  name: string | null;
  impressions: number;
  engagements: number;
  engagement_rate: number | null; // 0..1 fraction; null when impressions === 0
}
export interface EngagementAnalytics {
  rules: EngagementRuleAnalytics[];
  totals: {
    impressions: number;
    engagements: number;
    engagement_rate: number | null; // 0..1 fraction; null when impressions === 0
  };
}
export interface EngagementAnalyticsResponse {
  ok: boolean;
  result?: EngagementAnalytics;
  error?: string;
}
export interface EngagementRuleResponse {
  ok: boolean;
  result?: { rule: EngagementRule };
  error?: string;
}
export interface EngagementDeleteResponse {
  ok: boolean;
  result?: { deleted: boolean };
  error?: string;
}

// --- E2: operator-triggered outbound follow-up (single recipient) ----------
// Two payload shapes: an ad-hoc message (channel + subject?/body) OR a saved
// outbound template (rule_id) whose channel/subject/body the server applies.
export type EngagementSendStatus =
  | "sent"
  | "skipped_optout" // somebody said no; detail: "optout" | "ledger_block"
  // Nobody could be asked — NOT a refusal. detail: "consent_unavailable" |
  // "crm_unreachable" | "not_found".
  | "skipped_consent"
  | "skipped_tcpa" // SMS quiet-hours gate; detail: "no_timezone" | "tcpa_window"
  // SMS only, and both already returned by src/engagement/outbound.py — the
  // type was simply behind. detail: "over_budget" / "plan_inactive" | "suspended".
  | "skipped_budget"
  | "skipped_inactive"
  | "no_contact"
  | "invalid"
  | "failed";

export interface EngagementSendInlinePayload {
  session_id: string;
  channel: "email" | "sms";
  subject?: string;
  body: string;
}
export interface EngagementSendTemplatePayload {
  session_id: string;
  rule_id: string;
}
export type EngagementSendPayload =
  | EngagementSendInlinePayload
  | EngagementSendTemplatePayload;

export interface EngagementSendResponse {
  ok: boolean;
  result?: { status: EngagementSendStatus; detail?: string };
  error: string | null;
}

// --- B14/M34: the automated follow-up queues, and the operator's yes -------
// Batch E moved both re-engagement queues into Postgres. These two shapes are
// the only window onto them: the counts the Queue card's tiles show, and the
// steps a sequence is holding until somebody approves them.
//
// The two count objects are the store's own dicts, passed through unflattened
// so a new status key reaches the card without a wire change.
//
// `awaiting_approval` is not a status: it counts held STEPS inside rows that
// are still pending, which is the same unit `held` below lists. One sequence
// can be holding two of them. `held` is windowed and this number is not, so
// they agree on WHAT they count, not always on how many.
export interface EngagementEmailQueueCounts {
  pending: number;
  awaiting_approval: number;
  done: number;
  failed: number;
}
export interface EngagementSmsQueueCounts {
  pending: number;
  sent: number;
  skipped: number;
  failed: number;
  cancelled: number;
}

/** One step of a queued email sequence that is waiting for an operator's yes. */
export interface EngagementHeldStep {
  queue_key: string;
  session_id: string;
  contact_name: string | null;
  step_index: number;
  subject: string | null;
  send_at: string | null;
  requires_approval: boolean;
  approved_at: string | null;
  /** Sends already attempted for this step (a consent outage is retried). */
  attempts: number;
  // Three machine words, carried verbatim. The card renders whichever is set
  // through `sentence()` rather than showing a code to a human.
  last_error: string | null;
  template_error: string | null;
  skipped_reason: string | null;
}

/** One sequence that stopped for good. The tick marks a row `failed` and the
 *  store never leases it again, so it is never in `held` either -- before this
 *  it existed only inside the Failed tile's number. */
export interface EngagementQueueProblem {
  queue_key: string;
  session_id: string;
  contact_name: string | null;
  /** The row's own last error as a machine word (`claim_cap_reached`,
   *  `channel_off`, …). A row that stopped on a provider's own text reads
   *  `send_failed`: that text is never wire-borne, because nobody has read it
   *  before it would reach the card. Rendered through `sentence()`, never as
   *  a code. */
  last_error: string | null;
  /** Claims this row spent. Fifty means the crash guard retired it. */
  attempts: number;
  failed_at: string | null;
}

export interface EngagementQueueStatus {
  email: EngagementEmailQueueCounts;
  sms: EngagementSmsQueueCounts;
  /** At most 20 held steps, newest sequence first. `email.awaiting_approval`
   *  counts the same thing without the window. */
  held: EngagementHeldStep[];
  /** At most 10 failed sequences, most recently created first — which is not
   *  the order of the `failed_at` dates they show. `email.failed` counts the
   *  same thing without the window, and counts SMS rows the list cannot show. */
  problems: EngagementQueueProblem[];
}
export interface EngagementQueueStatusResponse {
  ok: boolean;
  result?: EngagementQueueStatus;
  error?: string | null;
}
export interface EngagementApproveStepResponse {
  ok: boolean;
  result?: { step: Record<string, unknown> };
  error?: string | null;
}

// ---------------------------------------------------------------------------
// Team seats v1 — owner-managed roster + pending invites (Settings).
// Contract is verbatim from docs/superpowers/plans/2026-07-01-team-seats.md.
// ---------------------------------------------------------------------------
export interface TeamMember {
  user_id: string;
  email: string;
  role: "owner" | "member";
  joined_at: string | null;
}
export interface TeamInvite {
  invite_id: string;
  email: string;
  invited_by: string;
  created_at: string | null;
  expires_at: string | null;
}
export interface TeamResponse {
  ok: boolean;
  result?: { you: { email: string; role: "owner" | "member" }; members: TeamMember[]; invites: TeamInvite[] };
  error?: string;
}
/** I-5: the owner-email handoff notice. Any member reads; only the owner writes. */
export interface TeamNotificationsResponse {
  ok: boolean;
  result?: { handoff_email: { enabled: boolean; can_edit: boolean } };
  error?: string;
}

export const teamApi = {
  list: () => api.get<TeamResponse>("/api/team"),
  notifications: () => api.get<TeamNotificationsResponse>("/api/team/notifications"),
  setHandoffEmail: (enabled: boolean) =>
    api.put<TeamNotificationsResponse>("/api/team/notifications", { handoff_email: enabled }),
  invite: (email: string) =>
    api.post<{ ok: boolean; result?: { status: string; invite_id: string }; error?: string }>(
      "/api/team/invites",
      { email },
    ),
  revokeInvite: (id: string) =>
    api.del<{ ok: boolean; error?: string }>(`/api/team/invites/${encodeURIComponent(id)}`),
  removeMember: (id: string) =>
    api.del<{ ok: boolean; error?: string }>(`/api/team/members/${encodeURIComponent(id)}`),
};

// ---------------------------------------------------------------------------
// Single sign-on (OIDC). Enterprise-only, owner-only. The client secret is
// write-only: it goes up on save and is never in any response, so an owner
// editing a domain allowlist sends no secret at all and the stored one stands.
// ---------------------------------------------------------------------------
export interface SsoConfig {
  configured: boolean;
  /** Always present: the callback URL to paste into the identity provider. */
  redirect_uri: string;
  slug?: string;
  enabled?: boolean;
  issuer?: string;
  client_id?: string;
  jit_provisioning?: boolean;
  allowed_email_domains?: string[];
  updated_at?: string | null;
  /** The URL to hand to staff. Present once configured. */
  sign_in_url?: string;
}
export interface SsoResponse {
  ok: boolean;
  result?: SsoConfig | null;
  error?: string;
  /** Per-field copy from a 400; the key is the field name it belongs against. */
  field_errors?: Record<string, string>;
  /** Present on a 402 when the plan does not include single sign-on. */
  upgrade_to?: string;
  status?: number;
}
export interface SsoInput {
  slug: string;
  issuer: string;
  client_id: string;
  /** Omit entirely to keep the stored secret. */
  client_secret?: string;
  enabled: boolean;
  jit_provisioning: boolean;
  allowed_email_domains: string[];
}

export const ssoApi = {
  get: (signal?: AbortSignal) => api.get<SsoResponse>("/api/sso", signal),
  save: (body: SsoInput) => api.put<SsoResponse>("/api/sso", body),
  remove: () => api.del<SsoResponse>("/api/sso"),
};

// ---------------------------------------------------------------------------
// Operator audit-log view v1 — read-only trail of sensitive actions
// (team changes, DSAR requests, outbound follow-ups), hash-chained per
// (tenant_id, stream) for tamper-evidence. Contract is verbatim from
// docs/superpowers/plans/2026-07-02-audit-log-view.md. `details` is already
// PII-redacted server-side at write time — safe to render as-is.
// prev_hash/idempotency_key/tenant_id are internal chain plumbing the API may
// still return but the operator UI never needs — omitted from this type on
// purpose.
// ---------------------------------------------------------------------------
export interface AuditEvent {
  ts: string | null;
  stream: string;
  event: string;
  actor: string;
  target: string;
  details: Record<string, unknown>;
  event_hash: string;
}
export interface AuditListResponse {
  ok: boolean;
  result?: { total: number; items: AuditEvent[] };
  error?: string | null;
}
export interface AuditVerifyResponse {
  ok: boolean;
  result?: {
    events: number;
    last_hash?: string;
    last_hash_by_stream?: Record<string, string>;
  };
  error?: string | null;
}

export const auditApi = {
  list: (stream?: string, limit = 50) =>
    api.get<AuditListResponse>(
      `/api/audit/events?${stream ? `stream=${encodeURIComponent(stream)}&` : ""}limit=${limit}`,
    ),
  verify: (stream?: string) =>
    api.get<AuditVerifyResponse>(
      `/api/audit/verify${stream ? `?stream=${encodeURIComponent(stream)}` : ""}`,
    ),
};

export const engagementApi = {
  list: () => api.get<EngagementListResponse>("/api/engagement/rules"),
  create: (body: EngagementRuleInput) =>
    api.post<EngagementRuleResponse>("/api/engagement/rules", body),
  update: (id: string, body: EngagementRuleInput) =>
    api.put<EngagementRuleResponse>(
      `/api/engagement/rules/${encodeURIComponent(id)}`,
      body,
    ),
  remove: (id: string) =>
    api.del<EngagementDeleteResponse>(
      `/api/engagement/rules/${encodeURIComponent(id)}`,
    ),
  // Per-rule + total impression/engagement counts over the trailing window.
  analytics: (days = 30) =>
    api.get<EngagementAnalyticsResponse>(
      `/api/engagement/analytics?days=${days}`,
    ),
  // E2: send one outbound follow-up to the open conversation's contact. The
  // backend gates on opt-out + per-tenant creds; a `skipped_optout`/`no_contact`
  // is a normal 200 outcome (not an error). 400 invalid, 401 unauth, 429 rate.
  send: (payload: EngagementSendPayload) =>
    api.post<EngagementSendResponse>("/api/engagement/send", payload),
  // B14: what the automated follow-ups are doing — counts for both queues plus
  // the steps still held for an operator's yes.
  queueStatus: () =>
    api.get<EngagementQueueStatusResponse>("/api/engagement/queue-status"),
  // M34: release one held step. 404 = nothing to approve (gone, or somebody
  // else already said yes); 409 = a processor is sending that row right now.
  approveStep: (queueKey: string, stepIndex: number) =>
    api.post<EngagementApproveStepResponse>(
      `/api/engagement/queue/email/${encodeURIComponent(queueKey)}/steps/${stepIndex}/approve`,
    ),
};
